import { generationHttpStatus, GENERATION_DISMISSED_EVENT, GENERATION_RECONNECT_MESSAGE, GENERATION_RESUME_MESSAGE } from '../utils/generationConnection';
import { generationFailureMessage } from '../utils/generationFailure';
import { isCustomGenerationType } from '@/utils/customGeneration';
/**
 * QuestionGenerationContext
 *
 * Global state management for bulk question generation progress tracking.
 *
 * Features:
 * - Backend job queue with browser-independent processing
 * - Server-driven recovery after refresh or on another device
 * - Cancellation support from anywhere in the app
 * - Real-time progress updates via polling
 * - Workbook metadata for display
 * - Multi-device support (cancel from any device)
 *
 * Architecture:
 * - Backend worker processes questions asynchronously
 * - Frontend polls job status every 5 seconds
 * - Recovery after reload/device switch comes from server-side generation_jobs
 *
 * Usage:
 * 1. Wrap app with <QuestionGenerationProvider>
 * 2. Use useQuestionGenerationContext() in components
 * 3. Call startGeneration() when beginning bulk generation (creates backend job)
 * 4. Context automatically polls for progress updates
 * 5. Call cancelGeneration() to cancel (works even after page refresh!)
 */

import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { QuestionGenerationProgress } from '@/types/question';
import type { CreateJobRequest, GenerationJob, GenerationJobKind, GenerationQueueReason } from '@/types/api';
import type { QuestionResult, DifficultyLevel } from '@/types/questionTypes';
import api, { generationJobsApi } from '@/services/api';
import { mocktestApi, type ManualRow, type MocktestGenerateRequest } from '@/services/mocktestApi';
import { oneclickApi, type GenerateRequest } from '@/services/oneclickApi';
import {
  ONECLICK_OCR_JOB_KIND,
  oneclickCompletedEvent,
  oneclickJobTitle,
  ONECLICK_JOB_KIND,
  isOneClickJobKind,
  notifyOneClickJobRemoved,
  oneclickDraftIdFromResults,
  modelVariantFields,
  type OneClickGenerationCompletedDetail,
} from '@/utils/oneclickJob';
import {
  ONECLICK_OCR_POINTS_PER_PAGE,
  oneclickGenerationPoints,
} from '@/utils/oneclickPoints';
import { generateTtsAndDownload } from '@/services/ttsService';
import { useToast } from '@/contexts/ToastContext';
import { getApiErrorMessage } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { debugEvent } from '@/lib/debugApi';
import type { TTSOptions } from '@/types';
import { buildConnectorAbsentToastMessage, countConnectorAbsentResults } from '@/utils/generatedQuestionResults';
import { isInsufficientCreditJob } from '@/utils/insufficientCredit';
import { evaluateStallState, type StallState } from './questionGenerationStall';
import { canRunParallelGenerationJobs } from '@/utils/parallelGeneration';
import { passageIdSeparatorError } from '@/components/dialogs/generationPassageIds';
import { useParallelGenerationJobs, type ParallelGenerationJob } from './useParallelGenerationJobs';

type PollingErrorType = 'notice' | 'cancelled' | 'generation_failed' | 'reconnecting' | 'fatal' | 'insufficient_credits' | 'stall_soft' | 'stall_hard' | null;

// States in which the job is known to have stopped: nothing is left to poll or cancel.
// ('fatal' is not one of them: it only means this browser lost the server.)
const TERMINAL_POLLING_ERROR_TYPES: ReadonlySet<PollingErrorType> = new Set<PollingErrorType>([
  'notice',
  'cancelled',
  'generation_failed',
  'insufficient_credits',
]);
const CANCELLED_ELSEWHERE_MESSAGE = '다른 기기 또는 탭에서 생성이 취소되었습니다.';
const SUPERSEDED_BY_RESUME_MESSAGE =
  '이 작업은 새로 시작된 작업에서 이어서 진행되고 있습니다. 새로고침하면 진행 상황을 다시 볼 수 있습니다.';

/** The server cancels an orphaned job with this reason when a resume job replaces it. */
function isSupersededJob(job: Pick<GenerationJob, 'error_message'>): boolean {
  return Boolean(job.error_message?.includes('Superseded'));
}
const CANCEL_REQUEST_FAILED_MESSAGE = '취소 요청에 실패했습니다. 다시 시도해주세요.';
const ONECLICK_RETRY_UNAVAILABLE_MESSAGE = '재시도할 수 있는 실패한 원클릭 출제 작업이 없습니다.';
const WORKBOOK_RETRY_UNAVAILABLE_MESSAGE = '재시도할 수 있는 실패한 교재 작업이 없습니다.';
const CANCEL_ORPHANED_MESSAGE = '중단된 작업을 멈추지 못했습니다. 잠시 후 다시 시도해주세요.';
const STOPPED_JOB_STATUSES: ReadonlySet<string> = new Set(['completed', 'failed', 'cancelled']);

/** `current_status` the server attaches when it refuses a cancel/delete (null when absent). */
function rejectedJobStatus(error: unknown): string | null {
  const details = (error as { details?: { current_status?: unknown } } | null)?.details;
  return typeof details?.current_status === 'string' ? details.current_status : null;
}

function isAlreadyStoppedRejection(error: unknown): boolean {
  const status = generationHttpStatus(error);
  const currentStatus = rejectedJobStatus(error);
  return status === 404 || (status === 400 && currentStatus !== null && STOPPED_JOB_STATUSES.has(currentStatus));
}

/**
 * Stop an orphaned job the server will not cancel. Orphaned jobs are
 * deletable; if the server resumed it in the meantime (delete refused as
 * queued/processing), it is cancellable again. True once nothing is running.
 */
async function stopOrphanedJob(jobId: string): Promise<boolean> {
  try {
    await generationJobsApi.deleteJob(jobId);
    return true;
  } catch (deleteError) {
    if (generationHttpStatus(deleteError) === 404) return true;
    const resumedStatus = rejectedJobStatus(deleteError);
    if (
      generationHttpStatus(deleteError) !== 400
      || (resumedStatus !== 'queued' && resumedStatus !== 'processing')
    ) {
      return false;
    }
  }
  try {
    await generationJobsApi.cancelJob(jobId);
    return true;
  } catch (cancelError) {
    return isAlreadyStoppedRejection(cancelError);
  }
}
const ORPHAN_RESUME_DELAY_MESSAGE =
  '작업 재개가 평소보다 오래 걸리고 있습니다. 완료된 문제는 안전하게 저장돼 있습니다. 계속 진행되지 않으면 닫기(X)로 작업을 중단한 뒤 남은 문제를 다시 생성해주세요.';
// How long an orphaned job may wait for server-side resume before the user is told.
const ORPHAN_RESUME_NOTICE_MS = 10 * 60 * 1000;
// A failed job is restored after a refresh only while it is recent.
const FAILED_JOB_RECOVERY_WINDOW_MS = 24 * 60 * 60 * 1000;

function finishedJobNoticeMessage(workbookId: number | null, jobKind?: string | null): string {
  if (isOneClickJobKind(jobKind)) {
    return '작업 조회가 종료되었습니다. 원클릭 모의고사 화면의 저장된 출제 결과를 확인해주세요.';
  }
  return workbookId === null
    ? '작업 조회가 종료되었습니다. 한 문제 제작 화면에서 저장된 결과를 확인해주세요.'
    : '작업 조회가 종료되었습니다. 교재에서 저장된 결과를 확인해주세요.';
}

/** Failed-job guidance: the job-level reason wins over the first item sample. */
function failedJobMessage(job: Pick<GenerationJob, 'error_message' | 'errors'>): string {
  return generationFailureMessage(job.error_message || job.errors?.[0]?.error);
}
type FailedGenerationRetryHandler = (job: GenerationJob) => Promise<boolean | void>;

export interface GenerationQueueInfo {
  isQueued: boolean;
  queuePosition: number;
  message?: string | null;
  reason?: GenerationQueueReason | null;
}

export interface ErrorDetail {
  passage_id: string;
  question_type: string;
  error: string;
  error_code?: string | null;  // Structured failure tag, e.g. 'insufficient_credits'
  traceback?: string;  // Backend traceback (administrators only on newer servers)
}

export interface QuestionGenerationState {
  isGenerating: boolean;
  shouldPoll: boolean;
  progress: QuestionGenerationProgress;
  isCompletionReady: boolean; // True only after frontend cache is synced with final generated data
  workbookId: number | null;
  workbookTitle: string | null;
  jobId: string | null; // Backend job ID for polling and cancellation
  // Workbook-less job flavor tracked by the context itself ('oneclick_mock'); null otherwise
  jobKind: GenerationJobKind | null;
  // One-click mock exam: the saved result draft once the job completed
  resultDraftId: number | null;
  targetSheetNames: string[]; // Expected sheet names that will receive questions
  questionToSheetMap: string[]; // Map of question index → sheet name
  // Queue tracking (for concurrent job prevention)
  isQueued: boolean; // True if job is waiting in queue
  queuePosition: number; // Position in queue (1 = first, 2 = second, etc.)
  queueMessage: string | null; // Server-provided reason for queueing, when known
  queueReason: GenerationQueueReason | null; // Structured queue reason (deployment gate/resume, capacity)
  // Results tracking
  successCount: number;
  errorCount: number;
  errorDetails: ErrorDetail[]; // Cached error details from job
  // API error tracking
  pollingError: string | null; // Error message when polling fails
  pollingErrorType: PollingErrorType; // Structured error type for UI rendering
}

export interface QuestionGenerationContextValue extends QuestionGenerationState {
  startGeneration: (workbookId: number | null, workbookTitle: string, jobId: string, targetSheetNames: string[], questionToSheetMap: string[], queueInfo?: GenerationQueueInfo, jobKind?: GenerationJobKind | null) => void;
  setGenerationState: (workbookId: number | null, workbookTitle: string, jobId: string, queueInfo?: GenerationQueueInfo) => void;
  setPollingError: (error: string | null, errorType?: PollingErrorType) => void;
  updateProgress: (progress: QuestionGenerationProgress, successCount?: number, errorCount?: number, errorDetails?: ErrorDetail[]) => void;
  finishGeneration: (errorCount?: number, jobId?: string, skipCleanup?: boolean) => void;
  cancelGeneration: (action?: string) => Promise<void>;
  clearState: () => void;
  retryPolling: () => void;
  /**
   * Retries the primary card's failed job. A one-click job is not re-posted:
   * the pending request is returned so the widget can confirm its points first
   * and then call submitOneClickRetry (SPEC-ONECLICK-BILLING-001 REQ-OCB-022).
   */
  retryFailedGeneration: () => Promise<OneClickRetryPending | void>;
  /** Sends a confirmed one-click retry; rejects (card unchanged) when the request fails. */
  submitOneClickRetry: (pending: OneClickRetryPending) => Promise<void>;
  setFailedGenerationRetryHandler: (handler: FailedGenerationRetryHandler | null) => void;
  setOnQuestionsGenerated: (callback: ((sheetIds: number[]) => void) | null) => void;
  /** Optional jobId scopes the one-shot handler so another job's finish never consumes it. */
  setOnGenerationFinished: (callback: ((event: GenerationFinishedEvent) => void) | null, jobId?: string) => void;
  setPendingFollowUpAction: (action: PersistedGenerationFollowUpAction | null) => void;
  getPendingFollowUpAction: () => PersistedGenerationFollowUpAction | null;
  setSingleQuestionActive: (active: boolean) => void;
  /** Jobs kept running beside the primary card (parallel-generation account only). */
  parallelJobs: ParallelGenerationJob[];
  cancelParallelJob: (jobId: string) => Promise<void>;
  dismissParallelJob: (jobId: string) => void;
  retryParallelJob: (jobId: string) => Promise<OneClickRetryPending | void>;
}

/**
 * A one-click retry waiting for the widget's points confirmation
 * (SPEC-ONECLICK-BILLING-001 REQ-OCB-020 (c), REQ-OCB-022). `points` is null
 * when an OCR job has no recorded page count (a charged retry is then blocked).
 */
export type OneClickRetryPending = {
  scope: 'primary' | 'parallel';
  sourceJobId: string;
} & (
  | { kind: typeof ONECLICK_JOB_KIND; request: GenerateRequest; points: number }
  | { kind: typeof ONECLICK_OCR_JOB_KIND; pages: number | null; points: number | null }
);

export interface GenerationFinishedEvent {
  status: 'completed' | 'cancelled' | 'failed';
  workbookId: number | null;
  workbookTitle: string | null;
  jobId: string | null;
}

interface MocktestAssignment {
  passageId?: number;
  passage_id?: string;
  passage?: string;
  translation?: string;
  problemType?: string;
  difficulty?: DifficultyLevel;
  rowOptions?: Record<string, unknown>;
}

interface FollowUpPassagePayload {
  passage_id: string;
  passage: string;
}

interface TtsFollowUpAction {
  kind: 'tts';
  passages: FollowUpPassagePayload[];
  ttsOptions: TTSOptions;
  workbookTitle?: string;
}

interface MocktestFollowUpAction {
  kind: 'mocktest';
  request: MocktestGenerateRequest;
}

interface TtsThenMocktestFollowUpAction {
  kind: 'tts_then_mocktest';
  passages: FollowUpPassagePayload[];
  ttsOptions: TTSOptions;
  workbookTitle?: string;
  request: MocktestGenerateRequest;
}

export type PersistedGenerationFollowUpAction =
  | TtsFollowUpAction
  | MocktestFollowUpAction
  | TtsThenMocktestFollowUpAction;

// Legacy jobs may have run a follow-up without a server claim. Only requests
// explicitly using this protocol can safely recover an unclaimed action.
export const FOLLOW_UP_CLAIM_PROTOCOL_VERSION = 1;

const ORPHAN_RECOVERY_GRACE_MS = 45000;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isMocktestAssignment(value: unknown): value is MocktestAssignment {
  return isRecord(value) && typeof value.problemType === 'string';
}

function isFollowUpPassagePayload(value: unknown): value is FollowUpPassagePayload {
  return isRecord(value)
    && typeof value.passage_id === 'string'
    && typeof value.passage === 'string';
}

function isPersistedGenerationFollowUpAction(value: unknown): value is PersistedGenerationFollowUpAction {
  if (!isRecord(value) || typeof value.kind !== 'string') {
    return false;
  }

  if (value.kind === 'tts') {
    return Array.isArray(value.passages)
      && value.passages.every(isFollowUpPassagePayload)
      && isRecord(value.ttsOptions);
  }

  if (value.kind === 'mocktest') {
    return isRecord(value.request) && typeof value.request.workbook_id === 'number';
  }

  if (value.kind === 'tts_then_mocktest') {
    return Array.isArray(value.passages)
      && value.passages.every(isFollowUpPassagePayload)
      && isRecord(value.ttsOptions)
      && isRecord(value.request)
      && typeof value.request.workbook_id === 'number';
  }

  return false;
}

function getPendingFollowUpActionFromJob(job: Pick<GenerationJob, 'options'>): PersistedGenerationFollowUpAction | null {
  const options = isRecord(job.options) ? job.options : null;
  const pendingFollowUpAction = options?._pending_follow_up_action;
  return isPersistedGenerationFollowUpAction(pendingFollowUpAction) ? pendingFollowUpAction : null;
}

function buildMocktestRetryRequest(job: GenerationJob): MocktestGenerateRequest | null {
  const options = isRecord(job.options) ? job.options : null;
  if (!options || options._mocktest !== true) {
    return null;
  }

  const rawAssignments = options._mocktest_assignments;
  if (!Array.isArray(rawAssignments) || rawAssignments.length === 0) {
    return null;
  }

  const assignments = rawAssignments.filter(isMocktestAssignment);
  if (assignments.length === 0 || assignments.length !== rawAssignments.length) {
    return null;
  }

  const passageMap = new Map<number, MocktestGenerateRequest['passages'][number]>();
  const rows: ManualRow[] = [];

  for (const assignment of assignments) {
    if (typeof assignment.passageId !== 'number' || assignment.passageId <= 0) {
      return null;
    }
    if (typeof assignment.passage_id !== 'string' || typeof assignment.passage !== 'string') {
      return null;
    }

    if (!passageMap.has(assignment.passageId)) {
      passageMap.set(assignment.passageId, {
        id: assignment.passageId,
        passage_id: assignment.passage_id,
        passage: assignment.passage,
        translation: typeof assignment.translation === 'string' ? assignment.translation : '',
      });
    }

    const row: ManualRow = {
      passageId: assignment.passageId,
      problemType: assignment.problemType!,
      // Preserve Easy on resume — coercing to Normal regenerated the
      // remaining Easy items of an interrupted job at the wrong difficulty.
      difficulty:
        assignment.difficulty === 'Hard' || assignment.difficulty === 'Easy'
          ? assignment.difficulty
          : 'Normal',
      editedPassage: assignment.passage,
      passageIdOverride: assignment.passage_id,
    };

    if (isRecord(assignment.rowOptions) && Object.keys(assignment.rowOptions).length > 0) {
      row.rowOptions = assignment.rowOptions;
    }

    rows.push(row);
  }

  if (passageMap.size === 0 || rows.length === 0 || job.workbook_id === null) {
    return null;
  }

  return {
    workbook_id: job.workbook_id,
    credit_retry_job_id: job.id,
    mode: 'manual',
    config: { rows },
    passages: Array.from(passageMap.values()),
    options: {
      ...(options as Record<string, unknown>),
      _mocktest_resume: true,
    },
    transformation: isRecord(job.transformation) ? job.transformation as Record<string, unknown> : undefined,
  };
}

/**
 * Same sample + passages (and per-passage transform options or the sample
 * originals choice) of a failed one-click job, or null when it cannot be retried.
 */
function buildOneClickRetryRequest(job: GenerationJob): GenerateRequest | null {
  const ocrDraftId = job.options?.ocr_draft_id;
  const passageIds = job.options?.passage_ids;
  const passageTransforms = job.options?.passage_transforms;
  if (
    job.status !== 'failed'
    || typeof ocrDraftId !== 'number'
    || !Array.isArray(passageIds)
    || passageIds.length === 0
    || !passageIds.every((id): id is number => typeof id === 'number')
  ) {
    return null;
  }
  const questionCount = job.options?.question_count;
  // REQ-OCV-011 (c): ask for the originals again; the server re-reads the sample's current links.
  const useSampleOriginals = job.options?.use_sample_originals === true;
  // Same model choice as the failed job; jobs stored before model_variant carry only the legacy flag.
  const storedVariant = job.options?.model_variant;
  const modelVariant = storedVariant === 'different_1' || storedVariant === 'different_2'
    ? storedVariant
    : job.options?.generate_differently === true ? 'different_1' : 'default';
  return {
    ...modelVariantFields(modelVariant),
    ocr_draft_id: ocrDraftId,
    passage_ids: passageIds,
    // REQ-OCB-022: keep the requested count; without it the retry would hold 160 points.
    ...(isPositiveInteger(questionCount) ? { question_count: questionCount } : {}),
    ...(useSampleOriginals ? { use_sample_originals: true } : {}),
    // Server-validated snapshot of the original request; sent back unchanged. Never together
    // with the originals (the server answers 400 sample_originals_with_passage_transforms).
    ...(!useSampleOriginals && isRecord(passageTransforms) && Object.keys(passageTransforms).length > 0
      ? { passage_transforms: passageTransforms as GenerateRequest['passage_transforms'] }
      : {}),
  };
}

function isPositiveInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

/**
 * The retry the widget confirms before sending (REQ-OCB-022): a generation
 * retry holds 4 points per requested question (160 when unspecified), an OCR
 * retry 2 points per recorded page. Null when a generation job cannot be retried.
 */
function buildOneClickRetryPending(
  job: GenerationJob,
  scope: OneClickRetryPending['scope'],
): OneClickRetryPending | null {
  if (job.job_kind === ONECLICK_OCR_JOB_KIND) {
    const pages = job.options?.pages;
    return {
      scope,
      sourceJobId: job.id,
      kind: ONECLICK_OCR_JOB_KIND,
      pages: isPositiveInteger(pages) ? pages : null,
      points: isPositiveInteger(pages) ? ONECLICK_OCR_POINTS_PER_PAGE * pages : null,
    };
  }
  const request = buildOneClickRetryRequest(job);
  if (!request) return null;
  return {
    scope,
    sourceJobId: job.id,
    kind: ONECLICK_JOB_KIND,
    request,
    points: oneclickGenerationPoints(request.question_count, Object.keys(request.passage_transforms ?? {}).length),
  };
}

/**
 * A retry that is not sent: the server would refuse it (api/mocktest.py and
 * api/generation_jobs.py answer 400 to a "//" passage number) with a sentence
 * naming no passage. The message names the numbers the user has to fix.
 */
class RetryRequestRefusedError extends Error {}

/**
 * Create the resume job for a failed workbook job: the mocktest retry when the
 * job was a mocktest, otherwise the same request with `_resume_generation`.
 *
 * The passage numbers persisted with the job are checked as the dialogs check
 * a new request: a "//" persisted before the server refused it is named here
 * instead of being re-sent on every press.
 */
async function createWorkbookRetryJob(job: GenerationJob): Promise<{
  jobId: string;
  total: number;
  mocktestSheetName: string | null;
}> {
  const mocktestRetryRequest = buildMocktestRetryRequest(job);
  const separatorError = passageIdSeparatorError(mocktestRetryRequest ? mocktestRetryRequest.passages : job.passages);
  if (separatorError) {
    throw new RetryRequestRefusedError(separatorError);
  }
  if (mocktestRetryRequest) {
    const newMocktestJob = await mocktestApi.generateMocktest(mocktestRetryRequest);
    return {
      jobId: newMocktestJob.job_id,
      total: newMocktestJob.total,
      mocktestSheetName: `실전모의고사 ${newMocktestJob.mocktest_number}회`,
    };
  }
  const retryRequest: CreateJobRequest = {
    credit_retry_job_id: job.id,
    workbook_id: job.workbook_id,
    sheet_id: job.sheet_id,
    passages: job.passages,
    question_types: job.question_types,
    difficulties: job.difficulties,
    ...(job.skip_types ? { skip_types: job.skip_types } : {}),
    // Approved changed-passage pairs; the server keeps only those still stale.
    ...(isRecord(job.options?._force_regenerate)
      ? { force_regenerate: job.options._force_regenerate as Record<string, string[]> }
      : {}),
    options: {
      ...(job.options ?? {}),
      _resume_generation: true,
    },
    transformation: job.transformation,
  };
  const newJob = await generationJobsApi.createJob(retryRequest);
  return {
    jobId: newJob.job_id,
    total: newJob.total,
    mocktestSheetName: null,
  };
}

const QuestionGenerationContext = createContext<QuestionGenerationContextValue | undefined>(undefined);

const POLLING_INTERVAL_MS = 5000; // Poll every 5 seconds

function createEmptyState(): QuestionGenerationState {
  return {
    isGenerating: false,
    shouldPoll: false,
    progress: {
      current: 0,
      total: 0,
      percentage: 0,
    },
    isCompletionReady: false,
    workbookId: null,
    workbookTitle: null,
    jobId: null,
    jobKind: null,
    resultDraftId: null,
    targetSheetNames: [],
    questionToSheetMap: [],
    isQueued: false,
    queuePosition: 0,
    queueMessage: null,
    queueReason: null,
    successCount: 0,
    errorCount: 0,
    errorDetails: [],
    pollingError: null,
    pollingErrorType: null,
  };
}

export function QuestionGenerationProvider({ children }: { children: ReactNode }) {
  // React Query client for cache invalidation
  const queryClient = useQueryClient();

  // Auth context for credit refresh and user state
  const { user, refreshCredits, updateCredits, isImpersonating } = useAuth();
  const { showError, showInfo, showSuccess, showWarning } = useToast();

  // Track user ID for server recovery and user-scoped SingleQuestion fallbacks
  const userIdRef = useRef<number | undefined>(user?.id);
  const mountedRef = useRef(true);
  const sessionEpochRef = useRef(0);
  const sessionImpersonatingRef = useRef(isImpersonating);
  const followUpAbortControllersRef = useRef(new Set<AbortController>());
  const followUpClaimsRef = useRef(new Map<string, { state: 'pending' | 'unconfirmed' }>());
  // Track impersonate state via ref for polling callback closures
  const isImpersonatingRef = useRef(isImpersonating);
  isImpersonatingRef.current = isImpersonating;

  const captureSessionScope = useCallback(() => {
    const epoch = sessionEpochRef.current;
    const owner = userIdRef.current;
    const observing = isImpersonatingRef.current;
    return () => mountedRef.current && sessionEpochRef.current === epoch
      && userIdRef.current === owner && isImpersonatingRef.current === observing;
  }, []);

  const stopFollowUps = useCallback(() => {
    for (const controller of followUpAbortControllersRef.current) controller.abort();
    followUpAbortControllersRef.current.clear();
    followUpClaimsRef.current.clear();
  }, []);

  // Sync credits from job polling response, falling back to async fetch
  const syncCreditsFromJob = useCallback((job: { remaining_credits?: number }) => {
    if (typeof job.remaining_credits === 'number') {
      updateCredits(job.remaining_credits);
    } else {
      refreshCredits();
    }
  }, [updateCredits, refreshCredits]);

  // One-click jobs hold points on 202 and settle them when they end: re-read the
  // balance and drop the cached point history (SPEC-ONECLICK-BILLING-001 REQ-OCB-021).
  // A ref keeps polling closures on the latest auth callback.
  const refreshCreditsRef = useRef(refreshCredits);
  refreshCreditsRef.current = refreshCredits;
  const refreshOneClickBilling = useCallback(() => {
    void refreshCreditsRef.current();
    void queryClient.invalidateQueries({ queryKey: ['creditHistory'] });
  }, [queryClient]);

  // Polling timeout reference (persists across renders)
  // Uses setTimeout chain (not setInterval) to allow dynamic delays during reconnection
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialPollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Auto-close timer scheduled by finishGeneration; cleared in stopPolling so
  // it cannot fire after unmount or a user switch
  const autoCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isPollingActiveRef = useRef(false);
  // Wall-clock of the last successful poll; used to detect a stalled setTimeout
  // chain (tab freeze / sleep / long network drop) so wake events can resume it.
  const lastPollAtRef = useRef<number>(0);
  const lastResumeAtRef = useRef<number>(0);
  // Monotonic token bumped on every (re)start/stop. A poll suspended at `await`
  // from a superseded chain compares against it to detect it lost ownership and
  // bail, preventing two interleaved polling chains from both advancing state.
  const pollGenerationRef = useRef(0);
  const orphanedSinceRef = useRef<number | null>(null);
  const singleQuestionActiveRef = useRef(false);
  const pendingFollowUpActionRef = useRef<PersistedGenerationFollowUpAction | null>(null);
  const executePendingFollowUpActionRef = useRef<(sourceJobId: string) => Promise<void>>(async () => {});
  const failedGenerationRetryInFlightRef = useRef(false);
  const failedGenerationRetryHandlerRef = useRef<FailedGenerationRetryHandler | null>(null);
  const workbookConnectorAbsentToastStateRef = useRef<{ jobId: string | null; count: number }>({
    jobId: null,
    count: 0,
  });

  // State ref for accessing current state in callbacks (avoids stale closure)
  const stateRef = useRef<QuestionGenerationState | null>(null);

  // Callback for notifying when questions are generated
  const onQuestionsGeneratedRef = useRef<((sheetIds: number[]) => void) | null>(null);
  const onGenerationFinishedRef = useRef<((event: GenerationFinishedEvent) => void) | null>(null);
  const onGenerationFinishedJobIdRef = useRef<string | null>(null);
  // Start of the current outage for a job. Kept outside the polling chain so a
  // focus/online restart of the chain cannot reset the 30-minute ceiling.
  const reconnectionRef = useRef<{ jobId: string; startedAt: number } | null>(null);

  const [state, setState] = useState<QuestionGenerationState>(createEmptyState);

  // Sync state to ref whenever it changes (fixes stale closure in callbacks)
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const setPendingFollowUpAction = useCallback((action: PersistedGenerationFollowUpAction | null) => {
    pendingFollowUpActionRef.current = action;
  }, []);

  const getPendingFollowUpAction = useCallback(() => pendingFollowUpActionRef.current, []);

  const setFailedGenerationRetryHandler = useCallback((handler: FailedGenerationRetryHandler | null) => {
    failedGenerationRetryHandlerRef.current = handler;
  }, []);

  // Parallel-generation account only: jobs replaced on the primary card keep
  // running and stay tracked as additional widget rows.
  const allowParallelJobsRef = useRef(false);
  allowParallelJobsRef.current = canRunParallelGenerationJobs(user);
  const parallelRetryInFlightRef = useRef(new Set<string>());

  const invalidateWorkbookQueries = useCallback((workbookId: number) => {
    queryClient.invalidateQueries({ queryKey: ['workbook', workbookId] });
    queryClient.invalidateQueries({ queryKey: ['sheets', workbookId] });
    queryClient.invalidateQueries({ queryKey: ['workbooks'] });
    queryClient.invalidateQueries({ queryKey: ['workbooks', 'recent'] });
  }, [queryClient]);

  const {
    parallelJobs,
    trackParallelJob,
    untrackParallelJob,
    patchParallelJob,
    captureParallelJobScope,
    resumeParallelJob,
    retargetParallelJob,
    cancelParallelJob,
    dismissParallelJob,
    clearParallelJobs,
  } = useParallelGenerationJobs({
    shouldAutoCleanup: (jobId) => !isImpersonatingRef.current && !followUpClaimsRef.current.has(jobId),
    onProgress: (job, entry) => {
      syncCreditsFromJob(job);
      if (entry.workbookId !== null) invalidateWorkbookQueries(entry.workbookId);
    },
    onFinished: async (job, entry) => {
      const isSameSession = captureSessionScope();
      const isCurrentRow = captureParallelJobScope(job.id);
      if (entry.workbookId !== null) {
        invalidateWorkbookQueries(entry.workbookId);
        if (job.status !== 'completed') return;
        syncCreditsFromJob(job);
        // The follow-up (TTS / mocktest) persisted on this job's options.
        const source = isRecord(job.options) ? job : await generationJobsApi.getJobDetails(job.id).catch(() => null);
        if (!isSameSession() || !isCurrentRow()) return;
        const followUpAction = source ? getPendingFollowUpActionFromJob(source) : null;
        if (followUpAction) {
          if (JSON.stringify(pendingFollowUpActionRef.current) === JSON.stringify(followUpAction)) {
            pendingFollowUpActionRef.current = null;
          }
          await executePendingFollowUpActionRef.current(job.id);
        }
        return;
      }
      if (isOneClickJobKind(job.job_kind ?? entry.jobKind)) {
        // Completed, failed or cancelled: the hold was settled (REQ-OCB-021).
        refreshOneClickBilling();
      }
      if (isOneClickJobKind(entry.jobKind) && job.status === 'completed') {
        window.dispatchEvent(new CustomEvent<OneClickGenerationCompletedDetail>(
          oneclickCompletedEvent(job.job_kind ?? entry.jobKind),
          { detail: { jobId: job.id, draftId: entry.resultDraftId } },
        ));
      }
    },
    stopJob: async (jobId) => {
      const isSameSession = captureSessionScope();
      try {
        await generationJobsApi.cancelJob(jobId);
        return isSameSession();
      } catch (error) {
        if (!isSameSession()) return false;
        const isOrphaned = generationHttpStatus(error) === 400 && rejectedJobStatus(error) === 'orphaned';
        const stopped = isAlreadyStoppedRejection(error) || (isOrphaned && await stopOrphanedJob(jobId));
        if (!isSameSession()) return false;
        if (!stopped) showError(isOrphaned ? CANCEL_ORPHANED_MESSAGE : CANCEL_REQUEST_FAILED_MESSAGE);
        return stopped;
      }
    },
  });

  /**
   * Stop only the job polling chain.
   *
   * State-driven effect cleanup uses this narrower helper so it does not
   * cancel the completion auto-close that finishGeneration just scheduled.
   */
  const stopPollingTimers = useCallback(() => {
    // Cancel initial timeout if still pending
    if (initialPollingTimeoutRef.current) {
      clearTimeout(initialPollingTimeoutRef.current);
      initialPollingTimeoutRef.current = null;
    }
    // Cancel polling timeout
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
      console.log('[QuestionGenerationContext] Stopped polling');
      debugEvent('QuestionGeneration.polling:stop');
    }
    isPollingActiveRef.current = false;
    pollGenerationRef.current += 1;
    orphanedSinceRef.current = null;
  }, []);

  const clearAutoCloseTimeout = useCallback(() => {
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }
  }, []);

  /**
   * Stop polling and cancel completion cleanup when the whole tracked job is
   * being replaced, cleared, or unmounted.
   */
  const stopPolling = useCallback(() => {
    stopPollingTimers();
    clearAutoCloseTimeout();
  }, [clearAutoCloseTimeout, stopPollingTimers]);

  const captureGenerationScope = useCallback(() => {
    const isSameSession = captureSessionScope();
    const generation = pollGenerationRef.current;
    const userId = userIdRef.current;
    const jobId = stateRef.current?.jobId;
    return () => isSameSession() && pollGenerationRef.current === generation
      && userIdRef.current === userId && stateRef.current?.jobId === jobId;
  }, [captureSessionScope]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      sessionEpochRef.current += 1;
      stopFollowUps();
      stopPolling();
    };
  }, [stopFollowUps, stopPolling]);

  // Detect user change (impersonation start/stop) and reload state
  useEffect(() => {
    const currentUserId = user?.id;
    const prevUserId = userIdRef.current;
    userIdRef.current = currentUserId;

    // Reset any in-memory tracking when the authenticated user changes.
    if (prevUserId !== currentUserId || sessionImpersonatingRef.current !== isImpersonating) {
      sessionImpersonatingRef.current = isImpersonating;
      sessionEpochRef.current += 1;
      stopFollowUps();
      pendingFollowUpActionRef.current = null;
      onGenerationFinishedRef.current = null;
      onGenerationFinishedJobIdRef.current = null;
      failedGenerationRetryHandlerRef.current = null;
      console.log(`[QuestionGenerationContext] User changed: ${prevUserId} → ${currentUserId ?? 'logged out'}, reloading state`);
      stopPolling();
      clearParallelJobs();
      workbookConnectorAbsentToastStateRef.current = { jobId: null, count: 0 };
      setState(createEmptyState());
    }
  }, [user?.id, isImpersonating, stopFollowUps, stopPolling, clearParallelJobs]);

  /**
   * Update generation progress
   *
   * @param progress Current progress state
   * @param successCount Number of successful generations (optional)
   * @param errorCount Number of failed generations (optional)
   * @param errorDetails Cached error details from job (optional)
   */
  const updateProgress = useCallback((
    progress: QuestionGenerationProgress,
    successCount?: number,
    errorCount?: number,
    errorDetails?: ErrorDetail[]
  ) => {
    setState(prev => {
      return {
        ...prev,
        progress,
        isCompletionReady:
          progress.total > 0 && progress.current < progress.total
            ? false
            : prev.isCompletionReady,
        successCount: successCount ?? prev.successCount,
        errorCount: errorCount ?? prev.errorCount,
        errorDetails: errorDetails ?? prev.errorDetails,
      };
    });
    console.log(`[QuestionGenerationContext] Progress: ${progress.current}/${progress.total} (${progress.percentage}%)`);
  }, []);

  /**
   * Mark generation as complete and clear state after delay
   * If there are errors, don't auto-close (user needs to review them)
   *
   * @param errorCount Number of errors (passed directly to avoid race condition with setState)
   * @param jobId Optional job ID to acknowledge when stateRef is not yet updated
   */
  const finishGeneration = useCallback((errorCount?: number, jobId?: string, skipCleanup?: boolean) => {
    if (jobId && stateRef.current?.jobId && stateRef.current.jobId !== jobId) return;
    console.log('[QuestionGenerationContext] Generation finished');
    skipCleanup = skipCleanup || isImpersonatingRef.current;

    // Stop polling
    stopPolling();

    // Check if there are errors - if so, don't auto-close
    // Use passed errorCount parameter to avoid race condition with setState
    const currentErrorCount = errorCount ?? stateRef.current?.errorCount ?? 0;
    const effectiveJobId = jobId ?? stateRef.current?.jobId;
    workbookConnectorAbsentToastStateRef.current = { jobId: null, count: 0 };

    if (currentErrorCount > 0) {
      console.log(`[QuestionGenerationContext] ${currentErrorCount} errors detected, keeping widget open for user review`);
      setState(prev => ({
        ...prev,
        shouldPoll: false,
      }));
      return;
    }

    // Keep state visible for 5 seconds before clearing (only if no errors).
    // The job is done, so it must not keep blocking other workbooks meanwhile.
    console.log('[QuestionGenerationContext] No errors, will auto-close in 5 seconds');
    setState(prev => (prev.shouldPoll ? { ...prev, shouldPoll: false } : prev));
    const closeCompletedCard = () => {
      autoCloseTimeoutRef.current = null;
      const claim = effectiveJobId ? followUpClaimsRef.current.get(effectiveJobId) : undefined;
      if (claim?.state === 'pending') {
        autoCloseTimeoutRef.current = setTimeout(closeCompletedCard, 5000);
        return;
      }
      const canCleanup = !skipCleanup && claim?.state !== 'unconfirmed';
      if (effectiveJobId && stateRef.current?.jobId && stateRef.current.jobId !== effectiveJobId) {
        if (effectiveJobId && canCleanup) {
          void generationJobsApi.deleteJob(effectiveJobId).catch((deleteError) => {
            console.warn('[QuestionGenerationContext] Failed to delete completed workbook job:', deleteError);
          });
        }
        return;
      }

      setState(createEmptyState());
      if (effectiveJobId && canCleanup) {
        void generationJobsApi.deleteJob(effectiveJobId).catch((deleteError) => {
          console.warn('[QuestionGenerationContext] Failed to delete completed workbook job:', deleteError);
        });
      }
    };
    autoCloseTimeoutRef.current = setTimeout(closeCompletedCard, 5000);
  }, [stopPolling]);

  /**
   * Replace error rows with newly generated questions
   * Called after job completes successfully
   */
  const replaceErrorRows = useCallback(async (workbookId: number) => {
    try {
      const storageKey = `error_row_replacements_${workbookId}`;
      sessionStorage.removeItem(storageKey);
    } catch (error) {
      console.error('[QuestionGenerationContext] Failed to clear error row replacement metadata:', error);
    }
  }, []);

  /**
   * Set polling error message
   * Used by external polling (e.g., SingleQuestion local polling) to display errors in GlobalProgressWidget
   *
   * @param error Error message to display (null to clear)
   */
  const setPollingError = useCallback((error: string | null, errorType?: PollingErrorType) => {
    setState(prev => {
      return {
        ...prev,
        pollingError: error,
        pollingErrorType: error === null ? null : (errorType ?? prev.pollingErrorType),
      };
    });
    console.log('[QuestionGenerationContext] Polling error set:', error, errorType ?? '');
  }, []);

  const setCompletionReady = useCallback((ready: boolean) => {
    setState(prev => {
      if (prev.isCompletionReady === ready) {
        return prev;
      }

      return {
        ...prev,
        isCompletionReady: ready,
      };
    });
  }, []);

  const waitForNextPaint = useCallback(async () => {
    await new Promise<void>((resolve) => {
      if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(() => resolve());
        return;
      }
      setTimeout(resolve, 0);
    });
  }, []);

  const syncWorkbookCompletion = useCallback(async (workbookId: number) => {
    const workbook = await queryClient.fetchQuery({
      queryKey: ['workbook', workbookId],
      queryFn: () => api.workbooks.getById(workbookId),
      staleTime: 0,
    });

    queryClient.invalidateQueries({ queryKey: ['sheets', workbookId] });
    queryClient.invalidateQueries({ queryKey: ['workbooks'] });
    queryClient.invalidateQueries({ queryKey: ['workbooks', 'recent'] });

    await waitForNextPaint();
    return workbook;
  }, [queryClient, waitForNextPaint]);

  /**
   * Reconcile a workbook job against the backend ledger when polling can no
   * longer trust the generation_jobs row (404 after deletion, or a stalled
   * poll loop). Counting is scoped to this job's ledger slots, so pre-existing
   * workbook rows from earlier jobs never produce a false completion.
   * Applies completion directly when the ledger says the work is done.
   */
  const reconcileJobState = useCallback(
    async (
      jobId: string,
      workbookId: number,
      isCurrent: () => boolean,
    ): Promise<'completed' | 'finished_unknown' | 'active' | 'terminal_incomplete' | 'unknown'> => {
      let r;
      try {
        r = await generationJobsApi.reconcileJob(jobId, workbookId);
      } catch {
        return 'unknown';
      }
      if (!isCurrent()) return 'unknown';
      if (r.is_complete && !r.job_exists) {
        // The row is gone (quick auto-cleanup of a clean completion, standard
        // retention expiry, or a cancel+delete from another tab/device). Nothing
        // is still running, but success is unknown: never present it as a clean 100%.
        stopPolling();
        queryClient.invalidateQueries({ queryKey: ['workbook', workbookId] });
        queryClient.invalidateQueries({ queryKey: ['sheets', workbookId] });
        setState(prev => ({
          ...prev,
          shouldPoll: false,
          pollingError: finishedJobNoticeMessage(workbookId),
          pollingErrorType: 'notice',
        }));
        return 'finished_unknown';
      }
      if (r.is_complete) {
        await syncWorkbookCompletion(workbookId);
        if (!isCurrent()) return 'unknown';
        // Preserve partial-failure state: a completion with error_count > 0 must
        // keep the widget open for review, not auto-close as a clean success.
        const completionErrors = r.error_count ?? 0;
        setState(prev => ({
          ...prev,
          progress: {
            ...prev.progress,
            current: prev.progress.total || prev.progress.current,
            percentage: 100,
          },
          isCompletionReady: true,
          errorCount: completionErrors,
          errorDetails: completionErrors > 0 ? (r.errors ?? prev.errorDetails) : [],
          pollingError: null,
          pollingErrorType: null,
        }));
        finishGeneration(completionErrors, jobId, true);
        return 'completed';
      }
      if (r.has_active_items || (r.job_exists && (r.job_status === 'processing' || r.job_status === 'queued'))) {
        return 'active';
      }
      if (r.job_exists && (r.job_status === 'failed' || r.job_status === 'cancelled')) {
        return 'terminal_incomplete';
      }
      return 'unknown';
    },
    [syncWorkbookCompletion, finishGeneration, stopPolling, queryClient]
  );

  /**
   * Start polling for job status updates
   *
   * Polling pattern: First poll after 2 seconds, then every 5 seconds
   *
   * @param jobId Backend job ID to poll
   */
  const startPolling = useCallback((jobId: string, immediate: boolean = false) => {
    // Clear existing polling if any
    stopPolling();

    console.log('[QuestionGenerationContext] Starting polling for job:', jobId);
    debugEvent('QuestionGeneration.polling:start', { jobId });
    isPollingActiveRef.current = true;
    // stopPolling() above bumped the generation; capture this chain's token.
    const myGeneration = pollGenerationRef.current;
    // Treat "just started" as a fresh poll so a wake event doesn't see it stale.
    lastPollAtRef.current = Date.now();

    let consecutiveErrorCount = 0;
    // Threshold before switching to reconnection mode (exponential backoff)
    const MAX_CONSECUTIVE_ERRORS = 40; // ~5 minutes at normal 5s polling

    // Reconnection mode: after MAX_CONSECUTIVE_ERRORS, use exponential backoff
    // instead of stopping permanently. Gives up after 30 minutes total.
    const MAX_RECONNECTION_MS = 30 * 60 * 1000; // 30 minutes
    // An outage that started before this chain (e.g. restarted on tab focus)
    // keeps its original start time; a different job starts fresh.
    if (reconnectionRef.current && reconnectionRef.current.jobId !== jobId) {
      reconnectionRef.current = null;
    }

    /**
     * Calculate polling delay based on error count.
     * - Normal (0 errors): 5 seconds
     * - Early errors (1-3): 10 seconds (quick recovery for transient issues)
     * - Mid errors (4-39): 15 seconds (steady retry without flooding)
     * - Reconnection (40+ errors): 15s → 20s → 25s → 30s cap
     */
    const getReconnectDelay = (errorCount: number): number => {
      if (errorCount === 0) return POLLING_INTERVAL_MS;
      if (errorCount <= 3) return 10000;
      if (errorCount < MAX_CONSECUTIVE_ERRORS) return 15000;
      const reconnectAttempt = errorCount - MAX_CONSECUTIVE_ERRORS;
      return Math.min(15000 + reconnectAttempt * 5000, 30000);
    };

    /** Schedule next poll with the given delay. No-op if polling was stopped. */
    const schedulePoll = (delay: number) => {
      if (!isPollingActiveRef.current || myGeneration !== pollGenerationRef.current) return;
      pollingTimeoutRef.current = setTimeout(pollFn, delay);
    };

    // SPEC-STALL-HEARTBEAT-001: Two-tier stall detection state
    // Tracks when completed last advanced (used to compute completedStaleMs).
    let stalledSince: number | null = null;     // null = not yet stale
    let prevStallState: StallState = 'none';    // last evaluated stall state

    // Define polling function (recursive setTimeout chain for dynamic delays)
    const pollFn = async () => {
      if (myGeneration !== pollGenerationRef.current) return;
      try {
        // §8.1 option 1: capture client time at response receipt to bound clock-skew
        // error to one polling interval (~5 s) instead of accumulated drift.
        const job = await generationJobsApi.getJob(jobId);
        // A newer startPolling() may have superseded this chain while awaiting;
        // bail so two interleaved chains don't both advance state / schedule polls.
        if (myGeneration !== pollGenerationRef.current) return;
        const pollReceivedAt = Date.now();
        lastPollAtRef.current = pollReceivedAt;
        // One-click mock exam jobs are workbook-less but owned by this context
        // (not SingleQuestion.tsx): the server kind wins over the tracked one.
        const isOneClickJob = isOneClickJobKind(job.job_kind ?? stateRef.current?.jobKind);
        if (isOneClickJob && job.job_kind && stateRef.current?.jobKind !== job.job_kind) {
          setState(prev => ({ ...prev, jobKind: job.job_kind! }));
        }

        if (!pendingFollowUpActionRef.current) {
          const restoredFollowUpAction = getPendingFollowUpActionFromJob(job);
          if (restoredFollowUpAction) {
            pendingFollowUpActionRef.current = restoredFollowUpAction;
          }
        }

        // Reset error counter and reconnection state on successful fetch
        consecutiveErrorCount = 0;
        if (
          reconnectionRef.current
          || orphanedSinceRef.current
          || stateRef.current?.pollingErrorType === 'reconnecting'
        ) {
          console.log('[QuestionGenerationContext] Server reconnected, resuming normal polling');
          // Only a poll that left 'orphaned' may clear the delay notice; an
          // orphaned poll re-evaluates it below (clearing here made it flicker).
          const clearOrphanDelay = orphanedSinceRef.current !== null && job.status !== 'orphaned';
          reconnectionRef.current = null;
          setState(prev => (
            prev.pollingErrorType === 'reconnecting'
            || (clearOrphanDelay && prev.pollingError === ORPHAN_RESUME_DELAY_MESSAGE)
          ) ? { ...prev, pollingError: null, pollingErrorType: null } : prev);
        }

        // Use stateRef to access current state (fixes stale closure bug)
        const previousCompleted = stateRef.current?.progress.current ?? 0;
        // hasNewQuestions is reused by stall detection, workbook refresh, and credit sync
        const hasNewQuestions = job.completed > previousCompleted;

        // Update progress with cached error details (important: cache before job deletion!)
        updateProgress(
          {
            current: job.completed,
            total: job.total,
            percentage: job.percentage
          },
          job.success_count,
          job.error_count,
          job.errors || []
        );

        // Queue reason/message arrive on poll responses too; keep the latest
        // server values while the job is still waiting (queued/orphaned) so a
        // reload or later poll never drops the deployment/capacity explanation.
        const polledQueueMessage = job.queue_message?.trim() || null;
        const polledQueueReason = job.queue_reason ?? null;
        if (job.status === 'queued' || job.status === 'orphaned') {
          setState(prev => {
            const nextQueued = job.status === 'queued' ? true : prev.isQueued;
            const nextMessage = polledQueueMessage ?? prev.queueMessage;
            const nextReason = polledQueueReason ?? prev.queueReason;
            // Newer servers send the live global position on every queued poll;
            // older servers omit it, in which case the last known value is kept.
            const nextPosition = job.status === 'queued' && typeof job.queue_position === 'number'
              ? Math.max(0, job.queue_position)
              : nextQueued ? prev.queuePosition || 1 : prev.queuePosition;
            if (
              nextQueued === prev.isQueued &&
              nextMessage === prev.queueMessage &&
              nextReason === prev.queueReason &&
              nextPosition === prev.queuePosition
            ) {
              return prev;
            }
            return {
              ...prev,
              isQueued: nextQueued,
              queuePosition: nextPosition,
              queueMessage: nextMessage,
              queueReason: nextReason,
            };
          });
        }

        // Clear queue status when job transitions from 'queued' to 'processing'
        if (
          job.status === 'processing' &&
          (stateRef.current?.isQueued || stateRef.current?.queueMessage || stateRef.current?.queueReason)
        ) {
          console.log('[QuestionGenerationContext] Job started processing, clearing queue status');
          setState(prev => ({
            ...prev,
            isQueued: false,
            queuePosition: 0,
            queueMessage: null,
            queueReason: null,
          }));
        }

        // STALL DETECTION: Two-tier logic using last_heartbeat_at + completed
        // (SPEC-STALL-HEARTBEAT-001, REQ-EVT-001 through REQ-EVT-004)
        if (job.completed > previousCompleted) {
          // Progress detected — reset completed-staleness timer
          stalledSince = null;
        } else if (stalledSince === null) {
          // First poll with no progress — start the timer
          stalledSince = pollReceivedAt;
        }

        const completedStaleMs = stalledSince !== null ? pollReceivedAt - stalledSince : 0;
        const stallState = evaluateStallState(
          isOneClickJob ? { ...job, job_kind: job.job_kind ?? stateRef.current?.jobKind } : job,
          pollReceivedAt,
          previousCompleted,
          completedStaleMs,
        );

        if (stallState !== prevStallState) {
          prevStallState = stallState;
          if (stallState === 'none') {
            // Clear stall warning (REQ-EVT-004) — only clear stall_soft / stall_hard
            const currentType = stateRef.current?.pollingErrorType;
            if (currentType === 'stall_soft' || currentType === 'stall_hard') {
              console.debug('[QuestionGenerationContext] Stall cleared — progress resumed');
              setState(prev => ({ ...prev, pollingError: null, pollingErrorType: null }));
            }
          } else if (stallState === 'soft') {
            const n = job.completed;
            const m = job.total;
            console.debug(`[QuestionGenerationContext] Soft stall: heartbeat fresh but completed stale for ${Math.round(completedStaleMs / 1000)}s`);
            setState(prev => ({
              ...prev,
              pollingError:
                `처리에 시간이 조금 걸리고 있어요.\n` +
                `(${n}/${m} 완료 · 긴 지문은 평소보다 조금 더 걸릴 수 있어요)\n` +
                `그대로 기다리시면 됩니다.`,
              pollingErrorType: 'stall_soft',
            }));
          } else {
            // stallState === 'hard'
            console.debug('[QuestionGenerationContext] Hard stall: heartbeat stale — showing recovery message');
            setState(prev => ({
              ...prev,
              pollingError:
                `서버 응답이 끊겨 자동 복구를 시도하고 있어요...\n` +
                `(완료된 항목은 안전하게 저장돼 있어요)`,
              pollingErrorType: 'stall_hard',
            }));
          }
        }

        if (hasNewQuestions) {
          orphanedSinceRef.current = null;
          console.log(`[QuestionGenerationContext] Progress: ${job.completed}/${job.total} questions completed`);
          // One-click points move on 202 and at the end, not per item; the
          // terminal branch below refreshes them (REQ-OCB-021).
          if (!isOneClickJob) syncCreditsFromJob(job);
        }

        // REAL-TIME UPDATE: Refetch workbook data when new questions are added
        const currentWorkbookId = stateRef.current?.workbookId;
        if (currentWorkbookId) {
          const toastState = workbookConnectorAbsentToastStateRef.current;
          const currentConnectorAbsentCount = countConnectorAbsentResults(job.results ?? []);
          if (toastState.jobId !== job.id) {
            toastState.jobId = job.id;
            toastState.count = 0;
          }
          if (currentConnectorAbsentCount > toastState.count) {
            showWarning(
              buildConnectorAbsentToastMessage(currentConnectorAbsentCount - toastState.count, 'workbook')
            );
          }
          toastState.count = currentConnectorAbsentCount;
        }
        if (hasNewQuestions && currentWorkbookId) {
          console.log(`[QuestionGenerationContext] New questions detected (${previousCompleted} → ${job.completed}), refreshing workbook`);
          queryClient.invalidateQueries({ queryKey: ['workbook', currentWorkbookId] });
          queryClient.invalidateQueries({ queryKey: ['workbooks'] });
          queryClient.invalidateQueries({ queryKey: ['workbooks', 'recent'] });

          // Trigger animation for the specific sheet that just received a question
          const questionMap = stateRef.current?.questionToSheetMap || [];
          if (questionMap.length > 0 && onQuestionsGeneratedRef.current) {
            // Get the sheet name for the question that just completed (0-indexed)
            const completedQuestionIndex = job.completed - 1;
            const targetSheetName = questionMap[completedQuestionIndex];

            if (targetSheetName) {
              // Fetch workbook to get sheet ID by name
              setTimeout(async () => {
                if (myGeneration !== pollGenerationRef.current) return;
                try {
                  const workbook = await api.workbooks.getById(currentWorkbookId);
                  if (myGeneration !== pollGenerationRef.current) return;
                  if (workbook && workbook.sheets) {
                    const targetSheet = workbook.sheets.find(s => s.name === targetSheetName);
                    if (targetSheet && onQuestionsGeneratedRef.current) {
                      console.log(`🎨 [QuestionGenerationContext] Real-time animation trigger for question ${job.completed}: "${targetSheetName}" (sheet ID: ${targetSheet.id})`);
                      onQuestionsGeneratedRef.current([targetSheet.id]);
                    }
                  }
                } catch (error) {
                  console.error('[QuestionGenerationContext] Failed to trigger real-time animation:', error);
                }
              }, 300); // Shorter delay for real-time feel
            }
          }
        }

        // Handle orphaned jobs (server restarted while job was processing)
        // Prefer server-side recovery first, then fall back to client retry only
        // if the orphaned status persists beyond a grace period.
        if (job.status === 'orphaned') {
          if (!orphanedSinceRef.current) {
            orphanedSinceRef.current = Date.now();
          }
          const orphanedDuration = Date.now() - orphanedSinceRef.current;

          // While the server reports a deployment resume it owns recovery; never
          // fall back to a client-side retry job in that window. A one-click
          // job is always resumed server-side (its payload is not a web job).
          if (
            orphanedDuration < ORPHAN_RECOVERY_GRACE_MS
            || polledQueueReason === 'deployment_resume'
            || isOneClickJob
            || isImpersonatingRef.current
          ) {
            console.log('[QuestionGenerationContext] Orphaned job detected, waiting for server-side recovery:', job.id);
            if (orphanedDuration >= ORPHAN_RESUME_NOTICE_MS) {
              // Keep polling (the server may still resume it), but stop implying
              // the wait is normal. Cleared by the next non-orphaned poll.
              if (stateRef.current?.pollingError !== ORPHAN_RESUME_DELAY_MESSAGE) {
                setPollingError(ORPHAN_RESUME_DELAY_MESSAGE, 'stall_hard');
              }
            } else if (polledQueueMessage) {
              // The server explanation is rendered by the neutral queue box
              // (queueMessage state above), not the error banner.
              if (stateRef.current?.pollingError) {
                setPollingError(null);
              }
            } else {
              setPollingError(GENERATION_RESUME_MESSAGE, 'reconnecting');
            }
            schedulePoll(POLLING_INTERVAL_MS);
            return;
          }

          console.log('[QuestionGenerationContext] Orphaned job recovery grace exceeded, falling back to client retry:', job.id);
          orphanedSinceRef.current = null;
          stopPolling();
          const isCurrentFallback = captureGenerationScope();
          const isSameFallbackSession = captureSessionScope();
          const fallbackTitle = stateRef.current?.workbookTitle || '문제 생성';

          try {
            const retryJob = await generationJobsApi.getJobDetails(job.id);
            if (!isCurrentFallback()) return;
            // The worker may have resumed/completed the job since the poll.
            if (retryJob.status !== 'orphaned') {
              startPolling(job.id, true);
              return;
            }
            const created = await createWorkbookRetryJob(retryJob);
            if (!isCurrentFallback()) {
              if (isSameFallbackSession() && allowParallelJobsRef.current) {
                trackParallelJob({
                  jobId: created.jobId, workbookId: retryJob.workbook_id,
                  workbookTitle: fallbackTitle, jobKind: retryJob.job_kind ?? null,
                  progress: { current: 0, total: created.total, percentage: 0 },
                });
              }
              return;
            }
            const newJobId = created.jobId;
            const newTotal = created.total;
            const nextTargetSheetNames = created.mocktestSheetName
              ? [created.mocktestSheetName] : stateRef.current?.targetSheetNames ?? [];
            const nextQuestionToSheetMap = created.mocktestSheetName
              ? Array.from({ length: newTotal }, () => created.mocktestSheetName!)
              : stateRef.current?.questionToSheetMap ?? [];

            // Update state with new job ID
            setState(prev => {
              return {
                ...prev,
                jobId: newJobId,
                progress: { current: 0, total: newTotal, percentage: 0 },
                pollingError: null,
                pollingErrorType: null,
                targetSheetNames: nextTargetSheetNames,
                questionToSheetMap: nextQuestionToSheetMap,
                successCount: 0,
                errorCount: 0,
                errorDetails: [],
                isCompletionReady: false,
                isQueued: false,
                queuePosition: 0,
                queueMessage: null,
                queueReason: null,
              };
            });

            // Start polling the new job
            startPolling(newJobId);
            return; // Don't continue with terminal status handling
          } catch (retryError) {
            if (!isCurrentFallback()) return;
            console.error('[QuestionGenerationContext] Failed to retry orphaned job:', retryError);
            const retryMsg = getApiErrorMessage(retryError, '서버 재시작 후 자동 재시도에 실패했습니다.');
            setPollingError(retryMsg + ' 다시 시도해주세요.');
            finishGeneration(1, job.id);
            return;
          }
        }

        orphanedSinceRef.current = null;

        // Stop polling if terminal status
        if (['completed', 'cancelled', 'failed'].includes(job.status)) {
          stopPolling();
          const completionGeneration = pollGenerationRef.current;
          const isCurrentCompletion = () => pollGenerationRef.current === completionGeneration
            && stateRef.current?.jobId === jobId;
          if (isOneClickJob) refreshOneClickBilling();

          // Notify one-shot completion callback (used for sequential workflows like TTS).
          // A handler scoped to another job stays registered for that job.
          const handlerJobId = onGenerationFinishedJobIdRef.current;
          const finishedHandler = handlerJobId && handlerJobId !== jobId
            ? null
            : onGenerationFinishedRef.current;
          if (finishedHandler) {
            onGenerationFinishedRef.current = null;
            onGenerationFinishedJobIdRef.current = null;
          }
          if (finishedHandler) {
            finishedHandler({
              status: job.status as GenerationFinishedEvent['status'],
              workbookId: stateRef.current?.workbookId ?? null,
              workbookTitle: stateRef.current?.workbookTitle ?? null,
              jobId: stateRef.current?.jobId ?? jobId,
            });
          } else if (job.status === 'completed' && currentWorkbookId != null) {
            setPendingFollowUpAction(null);
            void executePendingFollowUpActionRef.current(job.id);
          }
          if (!isCurrentCompletion()) return;

          // Clear transient polling errors on successful completion
          if (job.status === 'completed') {
            setPollingError(null);
          }

          // Handle successful completion
          if (job.status === 'completed' && currentWorkbookId) {
            // Replace error rows with new questions (if any)
            await replaceErrorRows(currentWorkbookId);
            if (!isCurrentCompletion()) return;

            try {
              const workbook = await syncWorkbookCompletion(currentWorkbookId);
              if (!isCurrentCompletion()) return;
              const targetNames = stateRef.current?.targetSheetNames || [];

              if (workbook && workbook.sheets && onQuestionsGeneratedRef.current && targetNames.length > 0) {
                const updatedSheets = workbook.sheets.filter(s => targetNames.includes(s.name));
                const sheetIds = updatedSheets.map(s => s.id);

                console.log('🎨 [QuestionGenerationContext] Target sheet names:', targetNames);
                console.log('🎨 [QuestionGenerationContext] Triggering animation for sheets:', sheetIds);
                onQuestionsGeneratedRef.current(sheetIds);
              }
            } catch (error) {
              if (!isCurrentCompletion()) return;
              console.error('[QuestionGenerationContext] Failed to sync workbook on completion:', error);
              queryClient.invalidateQueries({ queryKey: ['workbook', currentWorkbookId] });
              queryClient.invalidateQueries({ queryKey: ['sheets', currentWorkbookId] });
              queryClient.invalidateQueries({ queryKey: ['workbooks'] });
              queryClient.invalidateQueries({ queryKey: ['workbooks', 'recent'] });
            }

            setCompletionReady(true);

            syncCreditsFromJob(job);
          } else if (currentWorkbookId) {
            // For cancelled/failed jobs, still invalidate cache
            console.log('[QuestionGenerationContext] Invalidating workbook cache:', currentWorkbookId);
            queryClient.invalidateQueries({ queryKey: ['workbook', currentWorkbookId] });
            queryClient.invalidateQueries({ queryKey: ['sheets', currentWorkbookId] });
          } else if (isOneClickJob) {
            // The worker already saved the result as a one-click generation
            // draft; never run the SingleQuestion append path for it.
            if (job.status === 'completed') {
              const draftId = oneclickDraftIdFromResults(job.results);
              setState(prev => ({ ...prev, resultDraftId: draftId }));
              window.dispatchEvent(new CustomEvent<OneClickGenerationCompletedDetail>(
                oneclickCompletedEvent(job.job_kind ?? stateRef.current?.jobKind),
                { detail: { jobId: job.id, draftId } },
              ));
              setCompletionReady(true);
            }
          } else if (
            !currentWorkbookId &&
            job.status === 'completed' &&
            job.results &&
            job.results.length > 0 &&
            !singleQuestionActiveRef.current
          ) {
            // SingleQuestion job completed - save results to backend
            // This handles the case where SingleQuestion.tsx isn't mounted
            console.log('[QuestionGenerationContext] SingleQuestion job completed, saving results:', job.results.length);

            // Declare formattedResults outside try block so it's accessible in catch
            const formattedResults = job.results.map((result, index) => {
              const raw = result as QuestionResult & { id?: number; questionType?: string; difficulty?: string };
              const questionType = raw.questionType || 'Unknown';
              const difficulty = raw.difficulty || 'Normal';

              // Check if result needs parsing (raw AI format with "Answer:" marker)
              let question = raw.question || '';
              let answer = raw.answer || '';
              let explanation = raw.explanation || '';

              if (!isCustomGenerationType(questionType) && question.includes('Answer:')) {
                // Simple parsing - split on "Answer:" marker
                const parts = question.split('Answer:');
                question = parts[0].trim();
                if (parts[1]) {
                  const answerParts = parts[1].split('Explanation:');
                  answer = answerParts[0].trim();
                  if (answerParts[1]) {
                    explanation = answerParts[1].trim();
                  }
                }
              }

              return {
                id: typeof raw.id === 'number' ? raw.id : Date.now() + index,
                passage: raw.passage || '',
                question,
                answer,
                explanation,
                questionType,
                difficulty,
                timestamp: new Date().toISOString(),
                jobId: job.id,
              };
            });

            try {
              // Use append endpoint to avoid overwrite issues and allow de-dup on backend
              await api.post('/api/user/single-questions/append', { questions: formattedResults });
              if (!isCurrentCompletion()) return;
              console.log('[QuestionGenerationContext] SingleQuestion results appended successfully');
              try {
                if (userIdRef.current) localStorage.removeItem(`autoqm_single_question_job_id_${userIdRef.current}`);
              } catch (storageError) {
                console.warn('[QuestionGenerationContext] Failed to clear SingleQuestion jobId:', storageError);
              }
              syncCreditsFromJob(job);
              setCompletionReady(true);
            } catch (saveError) {
              if (!isCurrentCompletion()) return;
              console.error('[QuestionGenerationContext] Failed to save SingleQuestion results:', saveError);
              try {
                if (userIdRef.current) {
                  localStorage.setItem(`autoqm_single_questions_pending_${userIdRef.current}`, JSON.stringify(formattedResults));
                }
              } catch (storageError) {
                console.warn('[QuestionGenerationContext] Failed to store pending SingleQuestion results:', storageError);
              }
            }
          }

          if (!isCurrentCompletion()) return;
          if (job.status === 'failed') {
            setPollingError(
              failedJobMessage(job),
              isInsufficientCreditJob(job) ? 'insufficient_credits' : 'generation_failed'
            );
          }

          if (job.status === 'cancelled') {
            // This tab's own cancel stops polling before the request, so a polled
            // 'cancelled' means another tab/device (or a server auto-cancel) did it.
            // Keep the card open with an explicit notice instead of a live spinner.
            // A resume from another tab supersedes this job: the work goes on in
            // the new job, so that is not a cancellation.
            const superseded = isSupersededJob(job);
            setState(prev => ({
              ...prev,
              shouldPoll: false,
              pollingError: superseded ? SUPERSEDED_BY_RESUME_MESSAGE : CANCELLED_ELSEWHERE_MESSAGE,
              pollingErrorType: superseded ? 'notice' : 'cancelled',
            }));
            return;
          }

          // Pass errorCount directly to avoid race condition with setState
          finishGeneration(
            job.status === 'failed' ? Math.max(1, job.error_count) : job.error_count,
            job.id,
            job.status === 'completed' && job.job_kind === 'oneclick_ocr'
              && oneclickDraftIdFromResults(job.results) == null,
          );

          if (job.status === 'failed') {
            console.error('[QuestionGenerationContext] Job failed:', job.error_message);
          }
        } else {
          // Non-terminal status: schedule next poll at normal interval
          schedulePoll(POLLING_INTERVAL_MS);
        }
      } catch (error: unknown) {
        if (myGeneration !== pollGenerationRef.current) return;
        console.error('[QuestionGenerationContext] Polling error:', error);
        consecutiveErrorCount++;

        // Check if this is a 404 (job deleted or not found)
        if (generationHttpStatus(error) === 404) {
          console.log('[QuestionGenerationContext] Job not found (404), stopping polling');
          stopPolling();
          // A completed workbook job is auto-deleted, so a 404 may simply mean
          // "finished". Reconcile against the ledger before declaring failure.
          const workbookId404 = stateRef.current?.workbookId ?? null;
          notifyOneClickJobRemoved(jobId, stateRef.current?.jobKind);
          if (workbookId404 != null) {
            const stoppedGeneration = pollGenerationRef.current;
            const isCurrent = () => pollGenerationRef.current === stoppedGeneration;
            const outcome = await reconcileJobState(jobId, workbookId404, isCurrent);
            if (outcome === 'completed' || outcome === 'finished_unknown') {
              return;
            }
            if (!isCurrent()) return;
          }
          setState(prev => ({
            ...prev,
            shouldPoll: false,
            pollingError: finishedJobNoticeMessage(workbookId404, stateRef.current?.jobKind),
            pollingErrorType: 'notice',
            // Keep isGenerating: true so widget stays visible with error message
          }));
          return;
        }

        // Reconnection mode: after MAX_CONSECUTIVE_ERRORS, switch to exponential
        // backoff instead of stopping permanently. This handles GCE deployments
        // where the server goes down for 1-5 minutes and comes back.
        if (!reconnectionRef.current) {
          reconnectionRef.current = { jobId, startedAt: Date.now() };
          console.log(`[QuestionGenerationContext] Entering reconnection mode after ${consecutiveErrorCount} errors`);
        }
        if (stateRef.current?.pollingErrorType !== 'reconnecting') {
          setState(prev => ({
            ...prev,
            pollingError: GENERATION_RECONNECT_MESSAGE,
            pollingErrorType: 'reconnecting',
          }));
        }
        const reconnectionStartedAt = reconnectionRef.current.startedAt;

        // Check if max reconnection time exceeded (give up after 30 minutes)
        if (Date.now() - reconnectionStartedAt > MAX_RECONNECTION_MS) {
          console.log('[QuestionGenerationContext] Reconnection timeout exceeded, stopping polling');
          stopPolling();
          setState(prev => ({
            ...prev,
            pollingError: '서버 연결이 장시간 끊어져 있습니다. 페이지를 새로고침하거나 아래 재시도 버튼을 눌러주세요.',
            pollingErrorType: 'fatal',
          }));
          return;
        }

        // Schedule next poll with appropriate delay (normal or reconnection backoff)
        const nextDelay = getReconnectDelay(consecutiveErrorCount);
        if (reconnectionStartedAt) {
          const elapsed = Math.round((Date.now() - reconnectionStartedAt) / 1000);
          console.log(`[QuestionGenerationContext] Reconnection attempt (${elapsed}s elapsed), next poll in ${nextDelay / 1000}s`);
        } else {
          console.log(`[QuestionGenerationContext] Continuing polling (error count: ${consecutiveErrorCount}/${MAX_CONSECUTIVE_ERRORS})`);
        }
        schedulePoll(nextDelay);
      }
    };

    // First poll after 2 seconds (responsive for fast tasks),
    // then recursive setTimeout chain with dynamic delays
    const initialTimeout = setTimeout(() => {
      pollFn();
    }, immediate ? 0 : 2000);

    initialPollingTimeoutRef.current = initialTimeout;
  }, [
    captureSessionScope,
    captureGenerationScope,
    trackParallelJob,
    refreshOneClickBilling,
    stopPolling,
    updateProgress,
    finishGeneration,
    queryClient,
    replaceErrorRows,
    syncCreditsFromJob,
    setPollingError,
    setPendingFollowUpAction,
    setCompletionReady,
    syncWorkbookCompletion,
    reconcileJobState,
    showWarning,
  ]);
  // Note: Removed state.workbookId from deps - now using stateRef.current to avoid stale closure

  /**
   * A new job replacing a card whose job already stopped (failed, cancelled,
   * finished) deletes that job, exactly like dismissing it with X would.
   * Otherwise refresh recovery could restore the replaced card later.
   */
  const discardReplacedTerminalJob = useCallback((nextJobId: string) => {
    if (isImpersonatingRef.current) return;
    const previous = stateRef.current;
    if (!previous?.isGenerating || !previous.jobId || previous.jobId === nextJobId) return;
    const isStopped = !previous.shouldPoll
      || previous.isCompletionReady
      || TERMINAL_POLLING_ERROR_TYPES.has(previous.pollingErrorType);
    if (!isStopped) return;
    const replacedJobId = previous.jobId;
    if (followUpClaimsRef.current.has(replacedJobId)) return;
    void generationJobsApi.deleteJob(replacedJobId).catch((deleteError) => {
      console.warn('[QuestionGenerationContext] Failed to delete replaced job:', replacedJobId, deleteError);
    });
  }, []);

  /**
   * Parallel-generation account only: a still-running workbook or one-click
   * job about to be replaced on the primary card keeps being tracked as a
   * parallel row instead of silently dropping out of the widget.
   */
  const parkLivePrimaryJob = useCallback((nextJobId: string) => {
    // A row promoted back onto the primary card must not be polled twice.
    untrackParallelJob(nextJobId);
    if (!allowParallelJobsRef.current) return;
    const previous = stateRef.current;
    if (!previous?.isGenerating || !previous.jobId || previous.jobId === nextJobId) return;
    if (previous.workbookId === null && !isOneClickJobKind(previous.jobKind)) return;
    const isStopped = !previous.shouldPoll
      || previous.isCompletionReady
      || TERMINAL_POLLING_ERROR_TYPES.has(previous.pollingErrorType);
    if (isStopped) return;
    trackParallelJob({
      jobId: previous.jobId,
      workbookId: previous.workbookId,
      workbookTitle: previous.workbookTitle || (isOneClickJobKind(previous.jobKind) ? oneclickJobTitle(previous.jobKind) : '문제 생성'),
      jobKind: previous.jobKind,
      status: previous.isQueued ? 'queued' : 'processing',
      progress: previous.progress,
      successCount: previous.successCount,
      errorCount: previous.errorCount,
    });
  }, [trackParallelJob, untrackParallelJob]);

  /**
   * Start tracking question generation progress
   *
   * @param workbookId Target workbook ID (null for Single Question workflow)
   * @param workbookTitle Display name for workbook
   * @param jobId Backend job ID for polling and cancellation
   * @param targetSheetNames Expected sheet names that will receive questions
   * @param questionToSheetMap Map of question index → sheet name for precise animation
   * @param jobKind Workbook-less kind the context tracks itself ('oneclick_mock')
   */
  const startGeneration = useCallback((
    workbookId: number | null,
    workbookTitle: string,
    jobId: string,
    targetSheetNames: string[],
    questionToSheetMap: string[],
    queueInfo?: GenerationQueueInfo,
    jobKind?: GenerationJobKind | null
  ) => {
      const newState = {
      isGenerating: true,
      shouldPoll: true,
      progress: {
        current: 0,
        total: jobKind === ONECLICK_OCR_JOB_KIND ? 1 : 0,
        percentage: 0,
      },
      isCompletionReady: false,
      workbookId,
      workbookTitle,
      jobId,
      jobKind: jobKind ?? null,
      resultDraftId: null,
      targetSheetNames,
      questionToSheetMap,
      isQueued: queueInfo?.isQueued ?? false,
      queuePosition: queueInfo?.queuePosition ?? 0,
      queueMessage: queueInfo?.message?.trim() || null,
      queueReason: queueInfo?.reason ?? null,
      successCount: 0,
      errorCount: 0,
      errorDetails: [],
      pollingError: null,
      pollingErrorType: null,
    };

    parkLivePrimaryJob(jobId);
    discardReplacedTerminalJob(jobId);
    // React can batch several job-start responses into one render. Publish
    // the tracking snapshot now so the next start can park this job.
    stateRef.current = newState;
    setState(newState);
    orphanedSinceRef.current = null;
    workbookConnectorAbsentToastStateRef.current = { jobId, count: 0 };

    console.log('[QuestionGenerationContext] Started generation for:', workbookTitle, 'jobId:', jobId, 'target sheets:', targetSheetNames);
    console.log('[QuestionGenerationContext] Question-to-sheet map:', questionToSheetMap);
    if (queueInfo?.isQueued) {
      console.log('[QuestionGenerationContext] Job queued at position:', queueInfo.queuePosition);
    }

    // A one-click 202 (OCR job or generation) just held points (REQ-OCB-021).
    if (isOneClickJobKind(jobKind)) refreshOneClickBilling();

    // Start polling for progress updates
    startPolling(jobId);
  }, [discardReplacedTerminalJob, parkLivePrimaryJob, refreshOneClickBilling, startPolling]);

  /**
   * Set generation state and start polling
   * Used by SingleQuestion.tsx to show GlobalProgressWidget
   *
   * Previously this didn't start polling (SingleQuestion had its own local polling),
   * but this caused issues when navigating away - progress stopped updating.
   * Now both SingleQuestion's local polling and context polling may run simultaneously
   * while SingleQuestion is mounted (slight redundancy), but when SingleQuestion
   * unmounts, context polling continues to update progress.
   *
   * @param workbookId Target workbook ID (null for Single Question workflow)
   * @param workbookTitle Display name for workbook
   * @param jobId Backend job ID for display and polling
   */
  const setGenerationState = useCallback((
    workbookId: number | null,
    workbookTitle: string,
    jobId: string,
    queueInfo?: GenerationQueueInfo
  ) => {
    const newState = {
      isGenerating: true,
      shouldPoll: true,
      progress: {
        current: 0,
        total: 0,
        percentage: 0,
      },
      isCompletionReady: false,
      workbookId,
      workbookTitle,
      jobId,
      jobKind: null,
      resultDraftId: null,
      targetSheetNames: [],
      questionToSheetMap: [],
      isQueued: queueInfo?.isQueued ?? false,
      queuePosition: queueInfo?.queuePosition ?? 0,
      queueMessage: queueInfo?.message?.trim() || null,
      queueReason: queueInfo?.reason ?? null,
      successCount: 0,
      errorCount: 0,
      errorDetails: [],
      pollingError: null,
      pollingErrorType: null,
    };

    parkLivePrimaryJob(jobId);
    discardReplacedTerminalJob(jobId);
    stateRef.current = newState;
    setState(newState);
    orphanedSinceRef.current = null;
    workbookConnectorAbsentToastStateRef.current = { jobId, count: 0 };

    console.log('[QuestionGenerationContext] Set generation state:', workbookTitle, 'jobId:', jobId);
    if (queueInfo?.isQueued) {
      console.log('[QuestionGenerationContext] Job queued at position:', queueInfo.queuePosition);
    }

    // Start polling for progress updates
    // This ensures progress is tracked even when navigating away from SingleQuestion
    // Skip polling for SingleQuestion jobs when SingleQuestion page is active (local polling handles it)
    if (workbookId === null && singleQuestionActiveRef.current) {
      console.log('[QuestionGenerationContext] SingleQuestion job, local polling active - skipping context polling');
    } else {
      startPolling(jobId);
    }
  }, [discardReplacedTerminalJob, parkLivePrimaryJob, startPolling]);

  const executePendingFollowUpAction = useCallback(async (sourceJobId: string) => {
    if (!mountedRef.current || isImpersonatingRef.current) return;
    const isSameSession = captureSessionScope();
    const controller = new AbortController();
    followUpAbortControllersRef.current.add(controller);
    const claimState: { state: 'pending' | 'unconfirmed' } = { state: 'pending' };
    followUpClaimsRef.current.set(sourceJobId, claimState);
    try {
      const claim = await generationJobsApi.claimFollowUp(sourceJobId);
      if (followUpClaimsRef.current.get(sourceJobId) === claimState) {
        followUpClaimsRef.current.delete(sourceJobId);
      }
      if (!isSameSession() || !claim.claimed) return;
      if (!isPersistedGenerationFollowUpAction(claim.action)) {
        throw new Error('저장된 후속 작업 설정을 확인할 수 없습니다.');
      }
      // The grant and its payload belong to the same server job. A page's
      // pending selection may already describe a newer generation request.
      const action = claim.action;
      if (action.kind === 'tts' || action.kind === 'tts_then_mocktest') {
        if (action.ttsOptions.voice === 'alternating') {
          showWarning('남녀 교차 음성은 준비중입니다.');
        } else {
          showInfo('문제 생성 완료. 음성파일 생성 중...');
          const ttsResult = await generateTtsAndDownload(
            {
              passages: action.passages,
              options: action.ttsOptions,
              workbook_title: action.workbookTitle,
            },
            controller.signal,
          );
          if (!isSameSession()) return;
          if (ttsResult?.failedCount) {
            showWarning(`음성파일 ${ttsResult.successCount}개를 다운로드합니다. 실패한 ${ttsResult.failedCount}개 지문은 ZIP의 failures.json에서 확인하고 다시 시도해 주세요. 성공한 파일만 포인트가 차감되었습니다.`);
          } else {
            showSuccess('음성파일 다운로드를 시작합니다');
          }
          refreshCredits();
        }
      }

      if (action.kind === 'mocktest' || action.kind === 'tts_then_mocktest') {
        const response = await mocktestApi.generateMocktest(action.request);
        if (!isSameSession()) return;
        const sheetName = `실전모의고사 ${response.mocktest_number}회`;
        if (allowParallelJobsRef.current && stateRef.current?.shouldPoll && stateRef.current.jobId !== sourceJobId) {
          trackParallelJob({ jobId: response.job_id, workbookId: action.request.workbook_id,
            workbookTitle: `교재 #${action.request.workbook_id}`, jobKind: 'web_generation',
            progress: { current: 0, total: response.total, percentage: 0 } });
          return;
        }
        startGeneration(
          action.request.workbook_id,
          stateRef.current?.workbookTitle || `교재 #${action.request.workbook_id}`,
          response.job_id,
          [sheetName],
          Array.from({ length: response.total }, () => sheetName),
        );
        showSuccess(`실전모의고사 ${response.mocktest_number}회 출제가 시작되었습니다`);
      }
    } catch (error) {
      if (!isSameSession()) return;
      if (followUpClaimsRef.current.get(sourceJobId) === claimState) claimState.state = 'unconfirmed';
      showError(getApiErrorMessage(error, '후속 작업 실행에 실패했습니다.'));
    } finally {
      followUpAbortControllersRef.current.delete(controller);
    }
  }, [captureSessionScope, refreshCredits, showError, showInfo, showSuccess, showWarning, startGeneration, trackParallelJob]);

  useEffect(() => {
    executePendingFollowUpActionRef.current = executePendingFollowUpAction;
  }, [executePendingFollowUpAction]);

  /**
   * Inform the context when SingleQuestion page is mounted/unmounted.
   * Used to avoid duplicate polling and duplicate result persistence.
   */
  const setSingleQuestionActive = useCallback((active: boolean) => {
    singleQuestionActiveRef.current = active;

    const currentJobId = stateRef.current?.jobId;
    const currentWorkbookId = stateRef.current?.workbookId;
    const isSingleQuestionJob = currentWorkbookId === null
      && !isOneClickJobKind(stateRef.current?.jobKind);

    if (!isSingleQuestionJob || !currentJobId) {
      return;
    }

    if (active) {
      if (isPollingActiveRef.current) {
        stopPolling();
      }
      return;
    }

    if (!isPollingActiveRef.current && stateRef.current?.isGenerating) {
      if (!stateRef.current?.shouldPoll) {
        return;
      }
      startPolling(currentJobId);
    }
  }, [startPolling, stopPolling]);

  /**
   * Cancel ongoing generation
   * Calls backend API to cancel job and clears state
   */
  const cancelGeneration = useCallback(async (action = 'context_cancel') => {
    console.log('[QuestionGenerationContext] Generation cancelled by user');
    const current = stateRef.current;
    const currentJobId = current?.jobId;

    // Stop polling immediately
    stopPolling();
    const cancellationGeneration = pollGenerationRef.current;
    const isCurrentCancellation = () => pollGenerationRef.current === cancellationGeneration
      && stateRef.current?.jobId === currentJobId;

    // Check if generation is already over (overshoot counts as reached).
    // Use stateRef to access current state (fixes stale closure bug)
    const currentProgress = current?.progress;
    const isComplete = Boolean(
      currentProgress && currentProgress.total > 0 && currentProgress.current >= currentProgress.total
    );
    const isKnownTerminal = isComplete
      || Boolean(current?.isCompletionReady)
      || TERMINAL_POLLING_ERROR_TYPES.has(current?.pollingErrorType ?? null);

    if (currentJobId && !isKnownTerminal) {
      try {
        await generationJobsApi.cancelJob(currentJobId, action);
        if (!isCurrentCancellation()) return;
        console.log('[QuestionGenerationContext] Backend job cancelled:', currentJobId);
      } catch (error) {
        if (!isCurrentCancellation()) return;
        const isOrphaned = generationHttpStatus(error) === 400 && rejectedJobStatus(error) === 'orphaned';
        // The server only cancels queued/processing jobs; an orphaned job
        // (possibly one it can never resume) is stopped by deleting it.
        const alreadyStopped = isAlreadyStoppedRejection(error)
          || (isOrphaned && await stopOrphanedJob(currentJobId));
        if (!isCurrentCancellation()) return;
        if (!alreadyStopped) {
          // The job may still be running (and charging): keep the card and polling.
          console.error('[QuestionGenerationContext] Failed to cancel backend job:', error);
          showError(isOrphaned ? CANCEL_ORPHANED_MESSAGE : CANCEL_REQUEST_FAILED_MESSAGE);
          const latest = stateRef.current;
          if (
            latest?.jobId === currentJobId
            && latest.isGenerating
            && latest.shouldPoll
            && !(
              latest.workbookId === null
              && !isOneClickJobKind(latest.jobKind)
              && singleQuestionActiveRef.current
            )
          ) {
            startPolling(currentJobId, true);
          }
          return;
        }
        console.log('[QuestionGenerationContext] Job already stopped, clearing card:', currentJobId);
      }
      // The cancellation settled the hold (full refund or partial charge, REQ-OCB-021).
      if (isOneClickJobKind(current?.jobKind)) refreshOneClickBilling();
    } else if (isKnownTerminal) {
      console.log('[QuestionGenerationContext] Job already finished, skipping backend cancel call');
    }

    window.dispatchEvent(new CustomEvent(GENERATION_DISMISSED_EVENT, { detail: currentJobId }));
    // Clear any one-shot completion callback to avoid triggering on next job
    onGenerationFinishedRef.current = null;
    onGenerationFinishedJobIdRef.current = null;
    setPendingFollowUpAction(null);

    const pendingOcrResult = current?.jobKind === 'oneclick_ocr'
      && current.successCount > 0 && current.resultDraftId == null && isKnownTerminal;
    if (currentJobId && !pendingOcrResult && !followUpClaimsRef.current.has(currentJobId)) {
      try {
        await generationJobsApi.deleteJob(currentJobId, `${action}:cleanup`);
        console.log('[QuestionGenerationContext] Backend job deleted:', currentJobId);
      } catch (deleteError) {
        console.warn('[QuestionGenerationContext] Failed to delete backend job:', deleteError);
      }
    }

    if (isCurrentCancellation()) setState(createEmptyState());
  }, [refreshOneClickBilling, setPendingFollowUpAction, showError, startPolling, stopPolling]);
  // Note: Removed state.jobId from deps - now using stateRef.current to avoid stale closure

  /**
   * Clear state silently without showing any toast or completion message.
   * Used for silent cleanup of stale state (e.g., when recovering completed SingleQuestion jobs).
   */
  const clearState = useCallback(() => {
    // Stop polling
    stopPolling();
    onGenerationFinishedRef.current = null;
    onGenerationFinishedJobIdRef.current = null;
    setPendingFollowUpAction(null);
    workbookConnectorAbsentToastStateRef.current = { jobId: null, count: 0 };

    setState(createEmptyState());
  }, [setPendingFollowUpAction, stopPolling]);

  /**
   * Retry polling for the current job.
   * Used when auto-reconnection timed out and user clicks "재시도" button.
   * Checks if the job still exists and is in a resumable state before restarting polling.
   */
  const retryPolling = useCallback(async () => {
    const currentJobId = stateRef.current?.jobId;
    if (!currentJobId) return;
    const currentWorkbookId = stateRef.current?.workbookId;
    const isCurrent = captureGenerationScope();

    console.log('[QuestionGenerationContext] Manual retry polling for job:', currentJobId);
    // A manual retry starts a fresh 30-minute reconnection window.
    reconnectionRef.current = null;

    // Verify the job still exists and is resumable before restarting polling
    try {
      const job = await generationJobsApi.getJob(currentJobId);
      if (!isCurrent()) return;
      if (job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled') {
        // Terminal job: if the ledger says the work actually finished, show
        // completion instead of telling the user to regenerate.
        if (currentWorkbookId != null) {
          const outcome = await reconcileJobState(currentJobId, currentWorkbookId, isCurrent);
          if (outcome === 'completed' || outcome === 'finished_unknown') return;
          if (!isCurrent()) return;
        }
        setState(prev => ({
          ...prev,
          pollingError: '이전 작업이 이미 종료되었습니다. 새로 생성해주세요.',
          pollingErrorType: 'fatal',
        }));
        return;
      }
    } catch (error) {
      // The job row may have been deleted after completing — reconcile first.
      if (!isCurrent()) return;
      if (currentWorkbookId != null) {
        const outcome = await reconcileJobState(currentJobId, currentWorkbookId, isCurrent);
        if (outcome === 'completed' || outcome === 'finished_unknown') return;
        if (!isCurrent()) return;
      }
      if (generationHttpStatus(error) === 404) {
        notifyOneClickJobRemoved(currentJobId, stateRef.current?.jobKind);
        setState(prev => ({
          ...prev,
          shouldPoll: false,
          pollingError: finishedJobNoticeMessage(currentWorkbookId ?? null, stateRef.current?.jobKind),
          pollingErrorType: 'notice',
        }));
        return;
      }
      setState(prev => ({
        ...prev,
        pollingError: '서버에 연결할 수 없습니다. 잠시 후 다시 시도해주세요.',
        pollingErrorType: 'fatal',
      }));
      return;
    }

    setState(prev => ({ ...prev, pollingError: null, pollingErrorType: null }));
    startPolling(currentJobId);
  }, [captureGenerationScope, startPolling, reconcileJobState]);

  const retryFailedGeneration = useCallback(async (): Promise<OneClickRetryPending | void> => {
    const currentJobId = stateRef.current?.jobId;
    if (!currentJobId || failedGenerationRetryInFlightRef.current) return;
    failedGenerationRetryInFlightRef.current = true;
    let isCurrent = captureGenerationScope();
    const isSameRetrySession = captureSessionScope();
    const retryTitle = stateRef.current?.workbookTitle || '문제 생성';
    let creditFailure = false;
    // Once the resume job exists the failed source is superseded; remove it so a
    // later refresh does not restore the old failure card (terminal-job recovery).
    const deleteSupersededSourceJob = () => {
      void generationJobsApi.deleteJob(currentJobId).catch((deleteError) => {
        console.warn('[QuestionGenerationContext] Failed to delete superseded job:', deleteError);
      });
    };

    try {
      const job = await generationJobsApi.getJobDetails(currentJobId);
      if (!isCurrent()) return;
      creditFailure = isInsufficientCreditJob(job);
      if (job.status === 'processing' || job.status === 'queued' || job.status === 'orphaned') {
        setPollingError(null);
        startPolling(currentJobId);
        return;
      }

      const oneClickKind = job.job_kind ?? stateRef.current?.jobKind;
      if (isOneClickJobKind(oneClickKind)) {
        // A one-click retry is a new charged job: hand the request back so the
        // widget confirms its points, then submitOneClickRetry sends it (REQ-OCB-022).
        const pending = buildOneClickRetryPending({ ...job, job_kind: oneClickKind }, 'primary');
        if (!pending) {
          setPollingError(ONECLICK_RETRY_UNAVAILABLE_MESSAGE, 'fatal');
          return;
        }
        return pending;
      }

      const failedWorkbook = job.workbook_id != null &&
        (job.status === 'failed' || (job.status === 'completed' && job.error_count > 0));
      if (!creditFailure && !failedWorkbook) {
        setPollingError(WORKBOOK_RETRY_UNAVAILABLE_MESSAGE, 'fatal');
        return;
      }

      const customRetryHandler = failedGenerationRetryHandlerRef.current;
      if (job.workbook_id === null && customRetryHandler) {
        const handled = await customRetryHandler(job);
        if (!isCurrent()) return;
        if (handled !== false) {
          return;
        }
      }

      stopPolling();
      isCurrent = captureGenerationScope();
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current);
        autoCloseTimeoutRef.current = null;
      }

      const retryJob = await createWorkbookRetryJob(job);
      if (!isCurrent()) {
        if (isSameRetrySession() && allowParallelJobsRef.current) {
          trackParallelJob({
            jobId: retryJob.jobId, workbookId: job.workbook_id,
            workbookTitle: retryTitle, jobKind: job.job_kind ?? null,
            progress: { current: 0, total: retryJob.total, percentage: 0 },
          });
          deleteSupersededSourceJob();
        }
        return;
      }
      const newJobId = retryJob.jobId;
      const newTotal = retryJob.total;
      let nextTargetSheetNames = stateRef.current?.targetSheetNames ?? [];
      let nextQuestionToSheetMap = stateRef.current?.questionToSheetMap ?? [];

      if (retryJob.mocktestSheetName) {
        const sheetName = retryJob.mocktestSheetName;
        nextTargetSheetNames = [sheetName];
        nextQuestionToSheetMap = Array.from({ length: retryJob.total }, () => sheetName);
      }

      deleteSupersededSourceJob();
      setState(prev => ({
        ...prev,
        isGenerating: true,
        shouldPoll: true,
        jobId: newJobId,
        progress: { current: 0, total: newTotal, percentage: 0 },
        pollingError: null,
        pollingErrorType: null,
        targetSheetNames: nextTargetSheetNames,
        questionToSheetMap: nextQuestionToSheetMap,
        successCount: 0,
        errorCount: 0,
        errorDetails: [],
        isCompletionReady: false,
        isQueued: false,
        queuePosition: 0,
        queueMessage: null,
        queueReason: null,
      }));

      startPolling(newJobId);
    } catch (error) {
      if (!isCurrent()) return;
      if (error instanceof RetryRequestRefusedError) {
        // Nothing was sent: the failed card stays, with the numbers to fix.
        setPollingError(error.message, 'fatal');
        return;
      }
      const retryMsg = getApiErrorMessage(error, '재시도에 실패했습니다.');
      setPollingError(
        creditFailure ? `${retryMsg} 포인트 충전 여부를 확인한 뒤 다시 시도해주세요.` : retryMsg,
        creditFailure ? 'insufficient_credits' : 'generation_failed'
      );
    } finally {
      failedGenerationRetryInFlightRef.current = false;
    }
  }, [captureSessionScope, captureGenerationScope, setPollingError, startPolling, stopPolling, trackParallelJob]);

  /**
   * "남은 문제 재시도" for a parallel row: the same retry the primary card
   * runs, but the row (not the primary card) follows the resulting job.
   */
  const retryParallelJob = useCallback(async (jobId: string): Promise<OneClickRetryPending | void> => {
    if (parallelRetryInFlightRef.current.has(jobId)) return;
    parallelRetryInFlightRef.current.add(jobId);
    const isSameUser = captureSessionScope();
    const rowIsCurrent = captureParallelJobScope(jobId);
    const isCurrent = () => isSameUser() && rowIsCurrent();
    const previous = parallelJobs.find(entry => entry.jobId === jobId);
    patchParallelJob(jobId, { retrying: true, message: null });
    let creditFailure = false;
    try {
      const job = await generationJobsApi.getJobDetails(jobId);
      if (!isCurrent()) return;
      creditFailure = isInsufficientCreditJob(job);
      if (job.status === 'processing' || job.status === 'queued' || job.status === 'orphaned') {
        resumeParallelJob(jobId);
        return;
      }

      if (isOneClickJobKind(job.job_kind)) {
        // Confirmed by the widget's points dialog first (REQ-OCB-022).
        const pending = buildOneClickRetryPending(job, 'parallel');
        if (!pending) {
          patchParallelJob(jobId, { message: ONECLICK_RETRY_UNAVAILABLE_MESSAGE });
          return;
        }
        return pending;
      }

      const failedWorkbook = job.workbook_id != null &&
        (job.status === 'failed' || (job.status === 'completed' && job.error_count > 0));
      if (!creditFailure && !failedWorkbook) {
        patchParallelJob(jobId, { message: WORKBOOK_RETRY_UNAVAILABLE_MESSAGE });
        return;
      }

      const retryJob = await createWorkbookRetryJob(job);
      if (!isSameUser()) return;
      void generationJobsApi.deleteJob(jobId).catch((deleteError) => {
        console.warn('[QuestionGenerationContext] Failed to delete superseded job:', deleteError);
      });
      // A resume with nothing left completes at once; the row's next poll
      // reports it (and closes it) like any other completion.
      const seed = {
        jobId: retryJob.jobId,
        progress: { current: 0, total: retryJob.total, percentage: 0 },
      };
      if (!retargetParallelJob(jobId, seed)) {
        trackParallelJob({
          ...seed, workbookId: job.workbook_id,
          workbookTitle: previous?.workbookTitle || '문제 생성', jobKind: job.job_kind ?? null,
        });
      }
    } catch (error) {
      if (!isCurrent()) return;
      if (error instanceof RetryRequestRefusedError) {
        patchParallelJob(jobId, { message: error.message });
        return;
      }
      const retryMsg = getApiErrorMessage(error, '재시도에 실패했습니다.');
      patchParallelJob(jobId, {
        message: creditFailure ? `${retryMsg} 포인트 충전 여부를 확인한 뒤 다시 시도해주세요.` : retryMsg,
      });
    } finally {
      parallelRetryInFlightRef.current.delete(jobId);
      if (isCurrent()) patchParallelJob(jobId, { retrying: false });
    }
  }, [captureSessionScope, captureParallelJobScope, parallelJobs, patchParallelJob, resumeParallelJob, retargetParallelJob, trackParallelJob]);

  // @MX:NOTE: [AUTO] Sends a one-click retry only after the widget's points dialog
  // was confirmed (SPEC-ONECLICK-BILLING-001 REQ-OCB-022). On failure it rejects
  // and leaves the failed card / row untouched so the widget can show the reason
  // (402 → 포인트 충전하기); nothing is re-posted automatically (spec §9 L8).
  const submitOneClickRetry = useCallback(async (pending: OneClickRetryPending) => {
    const isSameRetrySession = captureSessionScope();
    const isCurrent = captureGenerationScope();
    const isOcr = pending.kind === ONECLICK_OCR_JOB_KIND;
    const created = isOcr
      ? await oneclickApi.retryOcr(pending.sourceJobId)
      : await oneclickApi.generate(pending.request);
    if (!isSameRetrySession()) return;
    // The retry is its own job with its own hold; the failed source is superseded.
    void generationJobsApi.deleteJob(pending.sourceJobId).catch((deleteError) => {
      console.warn('[QuestionGenerationContext] Failed to delete superseded job:', deleteError);
    });
    if (pending.scope === 'parallel' || (!isCurrent() && allowParallelJobsRef.current)) {
      refreshOneClickBilling();
      const seed = {
        jobId: created.job_id,
        jobKind: pending.kind,
        status: created.queued ? 'queued' as const : 'processing' as const,
        progress: { current: 0, total: created.total, percentage: 0 },
      };
      if (!retargetParallelJob(pending.sourceJobId, seed)) {
        trackParallelJob({ ...seed, workbookId: null, workbookTitle: oneclickJobTitle(pending.kind, Date.now()) });
      }
      return;
    }
    startGeneration(null, oneclickJobTitle(pending.kind, Date.now()), created.job_id, [], [], {
      isQueued: Boolean(created.queued),
      queuePosition: created.queue_position ?? 0,
      message: created.queue_message,
      reason: created.queue_reason,
    }, pending.kind);
  }, [captureSessionScope, captureGenerationScope, refreshOneClickBilling, retargetParallelJob, startGeneration, trackParallelJob]);

  // Cancelling a parallel one-click row settles its hold too (REQ-OCB-021).
  const cancelParallelJobWithBilling = useCallback(async (jobId: string) => {
    const kind = parallelJobs.find((job) => job.jobId === jobId)?.jobKind;
    await cancelParallelJob(jobId);
    if (isOneClickJobKind(kind)) refreshOneClickBilling();
  }, [cancelParallelJob, parallelJobs, refreshOneClickBilling]);

  /**
   * Set callback for when questions are generated
   *
   * @param callback Function to call with sheet IDs when questions are generated
   */
  const setOnQuestionsGenerated = useCallback((callback: ((sheetIds: number[]) => void) | null) => {
    onQuestionsGeneratedRef.current = callback;
  }, []);

  const setOnGenerationFinished = useCallback((
    callback: ((event: GenerationFinishedEvent) => void) | null,
    jobId?: string,
  ) => {
    onGenerationFinishedRef.current = callback;
    onGenerationFinishedJobIdRef.current = callback ? jobId ?? null : null;
  }, []);

  /**
   * Resume polling for the in-memory active job.
   * Cross-refresh/device recovery is handled separately via server orphan lookup.
   */
  useEffect(() => {
    if (state.isGenerating && state.jobId) {
      if (!state.shouldPoll) {
        return;
      }
      if (state.workbookId !== null || isOneClickJobKind(state.jobKind)) {
        // Resume polling for WORKBOOK and one-click jobs
        // SingleQuestion jobs (workbookId === null) are handled by SingleQuestion.tsx
        console.log('[QuestionGenerationContext] Resuming polling for job:', state.jobId);
        startPolling(state.jobId);
      } else {
        // SingleQuestion job - don't start polling here
        // SingleQuestion.tsx will handle it via local polling
        console.log('[QuestionGenerationContext] SingleQuestion job detected, deferring to SingleQuestion.tsx:', state.jobId);
      }
    }

    // Stop the previous polling chain when tracked job state changes. The
    // unmount-only effect above also cancels any pending completion auto-close.
    return () => {
      stopPollingTimers();
    };
  }, [state.isGenerating, state.shouldPoll, state.jobId, state.workbookId, state.jobKind, startPolling, stopPollingTimers]); // Watch for job changes and polling functions

  /**
   * Check for orphan WORKBOOK jobs on server that aren't tracked locally
   * SingleQuestion jobs are handled by SingleQuestion.tsx
   */
  /**
   * Display title for a recovered workbook job, from already-cached workbook
   * queries only (no extra fetch on page load). Falls back to "교재 #id".
   */
  const resolveWorkbookTitle = useCallback((workbookId: number | null): string => {
    if (workbookId === null) return '문제 생성';
    const cachedWorkbook = queryClient.getQueryData<{ title?: string }>(['workbook', workbookId]);
    if (cachedWorkbook?.title) return cachedWorkbook.title;
    const cachedList = queryClient.getQueryData<Array<{ id: number; title?: string }>>(['workbooks', userIdRef.current]);
    const listed = Array.isArray(cachedList) ? cachedList.find((workbook) => workbook.id === workbookId) : undefined;
    return listed?.title || `교재 #${workbookId}`;
  }, [queryClient]);

  const checkForOrphanWorkbookJobs = useCallback(async () => {
    // Skip if already generating
    if (stateRef.current?.isGenerating) return;
    const isCurrent = captureGenerationScope();

    try {
      debugEvent('QuestionGeneration.orphanCheck:start');
      // Fetch recent jobs (no status filter - get all active + recent completed)
      const response = await generationJobsApi.listJobs({ limit: 10 });
      if (!isCurrent()) return;

      // Find workbook jobs and one-click jobs (SingleQuestion.tsx recovers its own)
      const workbookJobs = response.jobs.filter(
        j => j.workbook_id !== null || isOneClickJobKind(j.job_kind)
      );
      if (workbookJobs.length === 0) return;

      const activeJob = workbookJobs.find(j =>
        j.status === 'queued' || j.status === 'processing' || j.status === 'orphaned'
      );

      if (activeJob) {
        console.log('[QuestionGenerationContext] Found orphan active workbook job:', activeJob.id);
        debugEvent('QuestionGeneration.orphanCheck:active', { jobId: activeJob.id });
        try {
          // List rows never touch the potentially-megabyte options ledger.
          // Hydrate retry/follow-up state once when recovering this one job.
          const activeJobDetails = await generationJobsApi.getJobDetails(activeJob.id);
          if (!isCurrent()) return;
          const pendingFollowUpAction = getPendingFollowUpActionFromJob(activeJobDetails);
          if (pendingFollowUpAction) {
            pendingFollowUpActionRef.current = pendingFollowUpAction;
          }
        } catch (detailError) {
          if (!isCurrent()) return;
          console.warn('[QuestionGenerationContext] Failed to hydrate active job details:', detailError);
        }
        const activeIsOneClick = isOneClickJobKind(activeJob.job_kind);
        const title = activeIsOneClick ? oneclickJobTitle(activeJob.job_kind, activeJob.created_at) : resolveWorkbookTitle(activeJob.workbook_id);
        let queueInfo: GenerationQueueInfo | undefined;
        if (activeJob.status === 'queued' && activeJob.workbook_id !== null) {
          try {
            const activeWorkbookResponse = await generationJobsApi.getActiveWorkbookJob(activeJob.workbook_id);
            if (activeWorkbookResponse.job?.id === activeJob.id) {
              queueInfo = {
                isQueued: true,
                queuePosition: activeWorkbookResponse.queue_position || 1,
                message: activeWorkbookResponse.queue_message ?? activeWorkbookResponse.job.queue_message,
                reason: activeWorkbookResponse.queue_reason ?? activeWorkbookResponse.job.queue_reason,
              };
            }
          } catch (queueInfoError) {
            console.warn('[QuestionGenerationContext] Failed to recover workbook queue position:', queueInfoError);
            queueInfo = {
              isQueued: true,
              queuePosition: 1,
            };
          }
        }
        if (!isCurrent()) return;
        startGeneration(
          activeJob.workbook_id,
          title,
          activeJob.id,
          [],
          [],
          queueInfo,
          activeIsOneClick ? activeJob.job_kind ?? null : null,
        );
        if (allowParallelJobsRef.current) {
          for (const otherJob of workbookJobs) {
            if (otherJob.id === activeJob.id) continue;
            if (otherJob.status !== 'queued' && otherJob.status !== 'processing' && otherJob.status !== 'orphaned') continue;
            const otherIsOneClick = isOneClickJobKind(otherJob.job_kind);
            trackParallelJob({
              jobId: otherJob.id,
              workbookId: otherJob.workbook_id,
              workbookTitle: otherIsOneClick ? oneclickJobTitle(otherJob.job_kind, otherJob.created_at) : resolveWorkbookTitle(otherJob.workbook_id),
              jobKind: otherIsOneClick ? otherJob.job_kind ?? null : null,
              status: otherJob.status,
              progress: { current: otherJob.completed, total: otherJob.total, percentage: otherJob.percentage },
              successCount: otherJob.success_count,
              errorCount: otherJob.error_count,
            });
          }
        }
        return;
      }

      if (isImpersonatingRef.current) return;

      // Restore the most recent terminal job (jobs are listed newest first): a
      // failed / credit-stopped card must survive a refresh just like a completion.
      // User cancellations are not restored, and failures only while recent.
      // A completion with errors counts as a failure here: the server keeps it
      // for days (for the error report), so it must not resurface for that long.
      const terminalJobSummary = workbookJobs.find(j => j.status === 'completed' || j.status === 'failed');
      const isCleanCompletion = terminalJobSummary?.status === 'completed' && !(terminalJobSummary.error_count > 0);
      const failedTimestamp = terminalJobSummary && !isCleanCompletion
        ? Date.parse(terminalJobSummary.completed_at || terminalJobSummary.updated_at || terminalJobSummary.created_at || '')
        : NaN;
      const isRecoverableTerminal = isCleanCompletion
        || (Number.isFinite(failedTimestamp) && Date.now() - failedTimestamp < FAILED_JOB_RECOVERY_WINDOW_MS);

      if (terminalJobSummary && isRecoverableTerminal) {
        // History rows are intentionally lightweight. A terminal-job recovery
        // needs the bounded error sample once.
        const completedJob = await generationJobsApi.getJobDetails(terminalJobSummary.id);
        if (!isCurrent()) return;
        console.log('[QuestionGenerationContext] Found terminal workbook job:', completedJob.id, completedJob.status);
        debugEvent('QuestionGeneration.orphanCheck:completed', { jobId: completedJob.id });
        // Recover only new-protocol jobs that never granted execution. The
        // server arbitrates concurrent recoveries; claimed/legacy jobs must
        // never be replayed because they may already have incurred charges.
        const options = isRecord(completedJob.options) ? completedJob.options : {};
        if (completedJob.status === 'completed'
          && options._follow_up_claim_protocol === FOLLOW_UP_CLAIM_PROTOCOL_VERSION
          && options._follow_up_claimed_job_id !== completedJob.id
          && getPendingFollowUpActionFromJob(completedJob)) {
          void executePendingFollowUpActionRef.current(completedJob.id);
        }
        const isFailedJob = completedJob.status === 'failed';
        const completedIsOneClick = isOneClickJobKind(completedJob.job_kind ?? terminalJobSummary.job_kind);
        const title = completedIsOneClick ? oneclickJobTitle(completedJob.job_kind ?? terminalJobSummary.job_kind, terminalJobSummary.created_at) : resolveWorkbookTitle(completedJob.workbook_id);

        // Show completion in GlobalProgressWidget with full error details
        // Don't use setGenerationState() as it starts polling - we already know job is complete
        setState({
          isGenerating: true, // Set true so widget shows, finishGeneration will set to false
          shouldPoll: false,
          progress: {
            current: completedJob.completed,
            total: completedJob.total,
            percentage: isFailedJob ? completedJob.percentage : 100,
          },
          isCompletionReady: !isFailedJob,
          workbookId: completedJob.workbook_id,
          workbookTitle: title,
          jobId: completedJob.id,
          jobKind: completedIsOneClick ? completedJob.job_kind ?? terminalJobSummary.job_kind ?? null : null,
          resultDraftId: completedIsOneClick ? oneclickDraftIdFromResults(completedJob.results) : null,
          targetSheetNames: [],
          questionToSheetMap: [],
          isQueued: false,
          queuePosition: 0,
          queueMessage: null,
          queueReason: null,
          successCount: completedJob.success_count,
          errorCount: completedJob.error_count,
          errorDetails: completedJob.errors || [],
          pollingError: isFailedJob ? failedJobMessage(completedJob) : null,
          pollingErrorType: isFailedJob
            ? (isInsufficientCreditJob(completedJob) ? 'insufficient_credits' : 'generation_failed')
            : null,
        });
        finishGeneration(
          isFailedJob ? Math.max(1, completedJob.error_count) : completedJob.error_count,
          completedJob.id,
          completedJob.status === 'completed' && completedJob.job_kind === 'oneclick_ocr'
            && oneclickDraftIdFromResults(completedJob.results) == null,
        );
      }
    } catch (error) {
      console.warn('[QuestionGenerationContext] Failed to check for orphan jobs:', error);
      debugEvent('QuestionGeneration.orphanCheck:error', {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }, [captureGenerationScope, startGeneration, finishGeneration, resolveWorkbookTitle, trackParallelJob]);

  /**
   * Check for orphan workbook jobs when user is authenticated.
   */
  useEffect(() => {
    // Check when user is authenticated and not already tracking a job
    if (!user || stateRef.current?.isGenerating || stateRef.current?.jobId) {
      return;
    }
    // Restore read-only tracking in impersonation too. The polling fallback
    // separately refuses new-job retries while impersonating.
    void checkForOrphanWorkbookJobs();
  }, [user, checkForOrphanWorkbookJobs, isImpersonating]);

  /**
   * Wake a stalled poll loop. The setTimeout polling chain can freeze when the
   * tab is backgrounded, the device sleeps, or the network drops for a long
   * time. On visibilitychange/online we reconcile against the ledger (so a job
   * that finished while we were away closes cleanly) and otherwise resume
   * polling immediately.
   */
  const resumePollingIfStale = useCallback(async () => {
    const s = stateRef.current;
    if (!s?.isGenerating || !s.jobId) return;
    // Only an actively polled job can be resumed. A finished, failed, cancelled
    // or given-up card must keep its state (e.g. "남은 문제 재시도") on tab focus.
    if (
      !s.shouldPoll
      || s.isCompletionReady
      || s.pollingErrorType === 'fatal'
      || TERMINAL_POLLING_ERROR_TYPES.has(s.pollingErrorType)
    ) {
      return;
    }

    const now = Date.now();
    if (now - lastResumeAtRef.current < 1000) return; // debounce burst events

    const isReconnecting = s.pollingErrorType === 'reconnecting';
    const stale = lastPollAtRef.current > 0 && now - lastPollAtRef.current > POLLING_INTERVAL_MS * 3;
    if (!isReconnecting && !stale) return;

    lastResumeAtRef.current = now;
    const isCurrent = captureGenerationScope();

    if (s.workbookId != null) {
      const outcome = await reconcileJobState(s.jobId, s.workbookId, isCurrent);
      if (outcome === 'completed' || outcome === 'finished_unknown') return;
      if (!isCurrent()) return;
      if (outcome === 'terminal_incomplete') {
        stopPolling();
        setState(prev => ({
          ...prev,
          pollingError: '일부 문제가 생성되지 않았습니다. 새로 생성해주세요.',
          pollingErrorType: 'fatal',
        }));
        return;
      }
      // 'active' or 'unknown': fall through and resume polling.
    }

    // Keep a live "reconnecting" banner: the outage clock continues across the
    // restart, and the next successful poll clears it.
    setState(prev => (prev.pollingErrorType === 'reconnecting'
      ? prev
      : { ...prev, pollingError: null, pollingErrorType: null }));
    startPolling(s.jobId, true);
  }, [captureGenerationScope, reconcileJobState, startPolling, stopPolling]);

  /**
   * Resume a stalled poll loop when the tab regains focus or the network comes
   * back. Without these listeners a frozen loop never recovered, leaving the
   * widget stuck on "서버 재연결 중" even after the work had finished.
   */
  useEffect(() => {
    const handleVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      void resumePollingIfStale();
    };
    const handleOnline = () => {
      void resumePollingIfStale();
    };
    document.addEventListener('visibilitychange', handleVisible);
    window.addEventListener('online', handleOnline);
    return () => {
      document.removeEventListener('visibilitychange', handleVisible);
      window.removeEventListener('online', handleOnline);
    };
  }, [resumePollingIfStale]);

  const value = useMemo<QuestionGenerationContextValue>(
    () => ({
      ...state,
      startGeneration,
      setGenerationState,
      setPollingError,
      updateProgress,
      finishGeneration,
      cancelGeneration,
      clearState,
      retryPolling,
      retryFailedGeneration,
      submitOneClickRetry,
      setFailedGenerationRetryHandler,
      setOnQuestionsGenerated,
      setOnGenerationFinished,
      setPendingFollowUpAction,
      getPendingFollowUpAction,
      setSingleQuestionActive,
      parallelJobs,
      cancelParallelJob: cancelParallelJobWithBilling,
      dismissParallelJob,
      retryParallelJob,
    }),
    [
      state,
      startGeneration,
      setGenerationState,
      setPollingError,
      updateProgress,
      finishGeneration,
      cancelGeneration,
      clearState,
      retryPolling,
      retryFailedGeneration,
      submitOneClickRetry,
      setFailedGenerationRetryHandler,
      setOnQuestionsGenerated,
      setOnGenerationFinished,
      setPendingFollowUpAction,
      getPendingFollowUpAction,
      setSingleQuestionActive,
      parallelJobs,
      cancelParallelJobWithBilling,
      dismissParallelJob,
      retryParallelJob,
    ]
  );

  return (
    <QuestionGenerationContext.Provider value={value}>
      {children}
    </QuestionGenerationContext.Provider>
  );
}

/**
 * Hook to access question generation context
 *
 * @throws Error if used outside QuestionGenerationProvider
 * @returns QuestionGenerationContextValue
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useQuestionGenerationContext() {
  const context = useContext(QuestionGenerationContext);
  if (!context) {
    throw new Error(
      'useQuestionGenerationContext must be used within QuestionGenerationProvider'
    );
  }
  return context;
}
